import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { apiError, fcfa, API } from "../lib/api";
import { AssistantWidget } from "../components/AssistantWidget";
import { CallButton } from "../components/VoiceProvider";
import { useAuth } from "../context/AuthContext";
import { useZone } from "../context/ZoneContext";
import { isNoldDomain } from "../lib/domain";
import { AdminZoneSwitch } from "../components/AdminZoneSwitch";
import { AppHeader } from "../components/AppHeader";
import { DashboardSideNav } from "../components/DashboardSideNav";
import RideMap from "../components/RideMap";
import { googleMapsDirUrl } from "../lib/geo";
import { SignContract } from "../components/SignContract"; // eslint-disable-line no-unused-vars
import ChatSheet from "../components/ChatSheet";
import { ProfileSetup } from "../components/ProfileSetup";
import { SelfieRecheck } from "../components/SelfieRecheck";
import { getCurrentPosition, acquireWakeLock, countryFromCoords, COUNTRY_CENTERS } from "../lib/geo";
import { enablePush, pushSupported, notifPermission } from "../lib/push";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Bike, Wallet, FileText, User, MapPin, Star, TrendingUp, Upload, Check, Clock, XCircle, Banknote, MessageCircle, Package, Car, Camera, Sparkles, QrCode, Smartphone, Phone, Receipt, ShieldCheck, Gift, BellRing, Sun, Trophy, Zap,
} from "lucide-react";
import QRCode from "qrcode";

const localMomoNum = (n) => (n || "").replace(/^\+?237/, "").replace(/\D/g, "");
const buildUssd = (op, num, amount) => {
  const ln = localMomoNum(num);
  if (!ln || op !== "mtn") return null; // MTN direct transfer only; Orange has no reliable one-shot
  return `*126*9*${ln}*${Math.round(amount)}#`;
};

// Client-side photo quality check (sharpness via Laplacian variance + brightness/glare).
async function analyzeImageQuality(dataUrl) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        const w = 256, h = Math.max(1, Math.round((img.height / img.width) * 256));
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);
        const gray = new Float64Array(w * h);
        let sum = 0, bright = 0;
        for (let i = 0, p = 0; i < data.length; i += 4, p++) {
          const g = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          gray[p] = g; sum += g;
          if (g > 245) bright++;
        }
        const mean = sum / (w * h);
        let lapSum = 0, lapSq = 0, n = 0;
        for (let y = 1; y < h - 1; y++) {
          for (let x = 1; x < w - 1; x++) {
            const idx = y * w + x;
            const lap = 4 * gray[idx] - gray[idx - 1] - gray[idx + 1] - gray[idx - w] - gray[idx + w];
            lapSum += lap; lapSq += lap * lap; n++;
          }
        }
        const variance = n ? (lapSq / n - (lapSum / n) ** 2) : 999;
        resolve({ blurry: variance < 60, dark: mean < 45, glare: bright / (w * h) > 0.12 });
      };
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    } catch { resolve(null); }
  });
}


// Compress/resize an image data URL to keep uploads small & reliable (avoids proxy body-size limits AND low-memory crashes on mobile).
async function compressImage(dataUrl, maxDim = 1000, quality = 0.6) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        let width = img.width, height = img.height;
        if (width > maxDim || height > maxDim) {
          if (width >= height) { height = Math.round((height / width) * maxDim); width = maxDim; }
          else { width = Math.round((width / height) * maxDim); height = maxDim; }
        }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    } catch { resolve(dataUrl); }
  });
}


const kycBadge = {
  not_submitted: { c: "#F26419", label: "notSubmitted" },
  pending: { c: "#F26419", label: "pending" },
  approved: { c: "#D94F2B", label: null },
  rejected: { c: "#D32F2F", label: "rejected" },
};

const VEHICLE_IMG = {
  africa: {
    moto: "https://images.unsplash.com/photo-1748808562719-a7114afb0d98?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    car: "https://images.unsplash.com/photo-1783815412573-fa748a0dcbab?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    bike: "https://images.unsplash.com/photo-1748808562719-a7114afb0d98?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
  europe: {
    moto: "https://images.unsplash.com/photo-1775224561039-f79250a1b6bb?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    car: "https://images.unsplash.com/photo-1730346056169-5ad76f8d456d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    bike: "https://images.unsplash.com/photo-1776167555860-ae1b7772bf9e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
};

export default function DriverDashboard() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const { zoneView, isSuperAdmin } = useZone();
  const [tab, setTab] = useState("rides");
  const [me, setMe] = useState(user);
  const [reviews, setReviews] = useState(null);
  const [showRecheck, setShowRecheck] = useState(false);
  const [available, setAvailable] = useState(user?.available || false);
  const [wakePending, setWakePending] = useState(false);
  const [gps, setGps] = useState({ state: "init" });
  const prevIncomingRef = useRef(null);
  const [, setSecTick] = useState(0);
  const [sub, setSub] = useState(null);
  const [subHistory, setSubHistory] = useState([]);
  const [activation, setActivation] = useState(null);
  const [actNumber, setActNumber] = useState("");
  const [actCode, setActCode] = useState("");
  const [actBusy, setActBusy] = useState(false);
  const [actDemo, setActDemo] = useState("");
  const [actCollab, setActCollab] = useState("");
  const loadActivation = () => api.get("/driver/activation").then((r) => setActivation(r.data)).catch(() => {});

  const loadSubHistory = () => {
    api.get("/driver/subscription/history").then((r) => setSubHistory(r.data)).catch(() => {});
  };

  const renewSub = async () => {
    try { const { data } = await api.post("/driver/subscription/renew"); setSub(data); toast.success(t("drv.subRenewed")); reload(); loadData(); loadSubHistory(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const waLink = (phone, msg) => {
    const digits = String(phone || "").replace(/[^0-9]/g, "");
    return `https://wa.me/${digits}?text=${encodeURIComponent(msg || "")}`;
  };

  const downloadSubReceipt = (h) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const date = h.created_at ? new Date(h.created_at).toLocaleString("fr-FR") : "";
    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Reçu Activation Mboa Go</title>
<style>body{font-family:system-ui,sans-serif;color:#1A1A1A;max-width:520px;margin:24px auto;padding:24px}
.h{display:flex;align-items:center;gap:10px;border-bottom:3px solid #D94F2B;padding-bottom:12px}
.h b{font-size:22px}.h .g{color:#F26419}
.row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee;font-size:14px}
.tot{font-size:20px;font-weight:800;margin-top:8px}.muted{color:#75807A}</style></head><body>
<div class="h"><b>Mboa<span class="g"> Go</span></b></div>
<h3>Reçu — Activation chauffeur</h3>
<div class="row"><span class="muted">Chauffeur</span><span>${esc(me?.name || "")} · ${esc(me?.phone || "")}</span></div>
<div class="row"><span class="muted">Réf.</span><span>${esc((h.id || "").slice(0, 8).toUpperCase())}</span></div>
<div class="row"><span class="muted">Date</span><span>${esc(date)}</span></div>
<div class="row"><span class="muted">Type</span><span>Activation unique</span></div>
<div class="row tot"><span>Montant payé</span><span>${(h.amount || 0).toLocaleString("fr-FR")} FCFA</span></div>
<p class="muted">Merci de faire partie de Mboa Go 🏍️</p></body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `recu-activation-${(h.id || "").slice(0, 8)}.html`; a.click();
    URL.revokeObjectURL(url);
  };
  const [incoming, setIncoming] = useState([]);
  const [popupRide, setPopupRide] = useState(null);
  const [popupLeft, setPopupLeft] = useState(15);
  const [kycSubmitting, setKycSubmitting] = useState(false);
  const [myRides, setMyRides] = useState([]);
  const [driverRateRide, setDriverRateRide] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [receiptsTotal, setReceiptsTotal] = useState(0);
  const [receiptsPeriod, setReceiptsPeriod] = useState("all");
  const [receiptsCash, setReceiptsCash] = useState(0);
  const [receiptsMomo, setReceiptsMomo] = useState(0);
  const [receiptsFrom, setReceiptsFrom] = useState("");
  const [receiptsTo, setReceiptsTo] = useState("");
  const [plate, setPlate] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [vehicleType, setVehicleType] = useState("");
  const [region, setRegion] = useState(() => {
    try { const s = localStorage.getItem("bsg_region"); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [promo, setPromo] = useState(null);
  const isEuropeDriver = region?.zone === "europe" || me?.zone === "europe";
  if (typeof localStorage !== "undefined" && region?.zone) { localStorage.setItem("bsg_zone", region.zone); try { localStorage.setItem("bsg_region", JSON.stringify(region)); } catch { /* ignore */ } }
  const promoDaysLeft = promo?.free_until ? Math.max(0, Math.ceil((new Date(promo.free_until) - new Date()) / 86400000)) : null;
  const euro = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const money = (v, cur) => (cur === "EUR" || isEuropeDriver) ? euro(v) : fcfa(v);
  const [addServiceMode, setAddServiceMode] = useState(false);
  const serviceOptions = isEuropeDriver
    ? [{ vt: "bike", label: "Livraison Colis", Icon: Bike }, { vt: "car", label: "Transport de Personnes", Icon: Car }]
    : [{ vt: "moto", label: t("delivery.modeMoto"), Icon: Bike }, { vt: "car", label: t("delivery.modeCar"), Icon: Car }, { vt: "comfort", label: t("delivery.modeComfort"), Icon: Car }, { vt: "bike", label: t("delivery.modeVelo"), Icon: Bike }];
  const carDocsRequired = (vehicleType === "car" || vehicleType === "comfort") && region?.free_period === false;
  const [iban, setIban] = useState("");
  const [euAddress, setEuAddress] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [euNom, setEuNom] = useState("");
  const [euPrenom, setEuPrenom] = useState("");
  const [taxiDocsRequired, setTaxiDocsRequired] = useState(false);
  const [docs, setDocs] = useState({ license_doc: "", id_card_doc: "", id_card_back_doc: "", vehicle_doc: "" });
  const [withdrawAmt, setWithdrawAmt] = useState("");
  const [rechargeInfo, setRechargeInfo] = useState(null);
  const [rechargeModal, setRechargeModal] = useState(null); // "orange_money" | "mtn_momo"
  const [rcAmount, setRcAmount] = useState("");
  const [rcTxId, setRcTxId] = useState("");
  const [rcProofPath, setRcProofPath] = useState("");
  const [rcProofPreview, setRcProofPreview] = useState("");
  const [rcProofUploading, setRcProofUploading] = useState(false);
  const [rcLoading, setRcLoading] = useState(false);
  const loadRechargeInfo = () => api.get("/driver/recharge-info").then((r) => setRechargeInfo(r.data)).catch(() => {});
  const openRecharge = (method) => { setRechargeModal(method); setRcAmount(""); setRcTxId(""); setRcProofPath(""); setRcProofPreview(""); };
  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRcProofUploading(true);
    setRcProofPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/driver/upload-proof", fd);
      setRcProofPath(data.proof_path);
    } catch (err) { toast.error(apiError(err)); setRcProofPreview(""); }
    finally { setRcProofUploading(false); }
  };
  const submitRecharge = async () => {
    const amount = Number(rcAmount);
    if (!amount || amount <= 0) { toast.error("Montant invalide."); return; }
    if (!rcTxId.trim()) { toast.error("L'ID de transaction (reçu par SMS) est obligatoire."); return; }
    if (!rcProofPath) { toast.error("La capture d'écran du reçu Mobile Money est obligatoire."); return; }
    setRcLoading(true);
    try {
      await api.post("/driver/wallet/recharge", { amount, method: rechargeModal, transaction_id: rcTxId.trim(), proof_path: rcProofPath });
      toast.success("Recharge enregistrée. En attente de validation par l'administrateur ✅");
      setRechargeModal(null);
      reload();
    } catch (err) { toast.error(apiError(err)); }
    finally { setRcLoading(false); }
  };
  const campayPay = async () => {
    const amount = Number(rcAmount);
    if (!amount || amount <= 0) { toast.error("Entrez le montant à recharger."); return; }
    setRcLoading(true);
    try {
      const { data } = await api.post("/wallet/campay/recharge", { amount, method: rechargeModal });
      if (data.pay_url) window.open(data.pay_url, "_blank");
      toast.info("Validez le paiement sur votre téléphone (Orange Money / MTN MoMo)…", { duration: 8000 });
      const uuid = data.uuid; let done = false;
      for (let i = 0; i < 24 && !done; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const s = await api.get(`/wallet/campay/status/${uuid}`);
          if (s.data.status === "completed") { done = true; toast.success("Paiement reçu ✅ Portefeuille crédité."); setRechargeModal(null); reload(); }
          else if (["failed", "cancelled"].includes(s.data.status)) { done = true; toast.error("Paiement échoué ou annulé."); }
        } catch {}
      }
      if (!done) toast.message("En attente de confirmation… votre solde se mettra à jour dès réception.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setRcLoading(false); }
  };
  const [driverPos, setDriverPos] = useState(null);
  const [batterySaver, setBatterySaver] = useState(() => localStorage.getItem("bsg_battery_saver") === "1");
  const [wakeOn, setWakeOn] = useState(() => localStorage.getItem("bsg_wake_on") === "1");
  const [notifOn, setNotifOn] = useState(() => notifPermission() === "granted");
  const lastCoordsRef = useRef(null);
  const wlRef = useRef(null);
  const [gami, setGami] = useState(null);
  const [board, setBoard] = useState(null);
  const [adminMsg, setAdminMsg] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [payMethod, setPayMethod] = useState(null); // null | "cash" | "mobile"
  const [qr, setQr] = useState({ orange: "", mtn: "", active: "" });
  const [qrNum, setQrNum] = useState({ orange: "", mtn: "" });
  useEffect(() => { if (me) { setQr({ orange: me.payment_qr_orange || "", mtn: me.payment_qr_mtn || "", active: me.payment_qr_active || "" }); setQrNum({ orange: me.payment_num_orange || "", mtn: me.payment_num_mtn || "" }); } }, [me]);

  const status = me?.kyc_status || "not_submitted";
  const approved = status === "approved";

  const reload = async () => {
    const u = await refresh();
    setMe(u); setAvailable(u.available || false);
    api.get("/driver/subscription").then((r) => setSub(r.data)).catch(() => {});
    api.get("/kyc-policy").then((r) => setTaxiDocsRequired(!!r.data.taxi_docs_required)).catch(() => {});
    loadSubHistory();
  };

  const downloadReceipt = async (id) => {
    try {
      const r = await api.get(`/driver/receipts/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `recu-${id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur PDF"); }
  };

  const loadData = () => {
    api.get("/rides/mine").then((r) => setMyRides(r.data)).catch(() => {});
    api.get("/driver/earnings").then((r) => setEarnings(r.data)).catch(() => {});
    loadRechargeInfo();
    loadActivation();
    api.get(`/driver/receipts?period=${receiptsPeriod}`).then((r) => { setReceipts(r.data.items || []); setReceiptsTotal(r.data.total || 0); setReceiptsCash(r.data.total_cash || 0); setReceiptsMomo(r.data.total_momo || 0); }).catch(() => {});
    if (approved) api.get("/driver/available-rides").then((r) => setIncoming(r.data)).catch(() => {});
    if (approved) api.get("/driver/pending-wake").then((r) => setWakePending(!!r.data?.pending)).catch(() => {});
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);
  // Intent « Devenir Chauffeur/Livreur » : ouvre le formulaire KYC + pré-sélectionne le véhicule.
  useEffect(() => {
    const intent = localStorage.getItem("bsg_driver_intent");
    if (intent) {
      setTab("kyc");
      setVehicleType(intent === "delivery" ? "bike" : "moto");
      localStorage.removeItem("bsg_driver_intent");
    }
  }, []);
  // Détection de zone (Europe/Afrique) — INSTANTANÉE par IP, puis affinage GPS facultatif.
  useEffect(() => {
    (async () => {
      // Zone forcée : domaine noldgo.com → Europe ; sinon Vue Admin (super-admin) — prioritaire sur IP/GPS.
      const forcedZone = isNoldDomain() ? "europe" : (isSuperAdmin && zoneView ? zoneView : null);
      if (forcedZone) {
        const cc = forcedZone === "europe" ? "BE" : "CM";
        try { const { data } = await api.get(`/region/config?country_code=${cc}`); setRegion(data); if (forcedZone === "europe") setVehicleType((v) => v || "bike"); } catch { /* ignore */ }
        return;
      }
      try {
        const { data } = await api.get(`/region/detect`);
        if (data?.zone) { setRegion(data); if (data.zone === "europe") setVehicleType((v) => v || "bike"); }
      } catch { /* ignore */ }
      try {
        const c = await getCurrentPosition();
        const cc = await countryFromCoords(c.lat, c.lng);
        const { data } = cc ? await api.get(`/region/config?country_code=${cc}`) : await api.get(`/region/detect?lat=${c.lat}&lng=${c.lng}`);
        setRegion(data); if (data.zone === "europe") setVehicleType((v) => v || "bike");
      } catch { /* défaut Afrique / IP */ }
    })();
  }, [zoneView]); // eslint-disable-line

  // Période de lancement gratuite (dépôt offert) — pour la bannière chauffeur.
  useEffect(() => { api.get("/promo").then((r) => setPromo(r.data)).catch(() => {}); }, []);

  // Pré-remplit Nom/Prénom depuis le compte (inscription chauffeur Europe).
  useEffect(() => {
    if (isEuropeDriver && me?.name && !euPrenom && !euNom) {
      const parts = me.name.trim().split(/\s+/);
      setEuPrenom(parts[0] || "");
      setEuNom(parts.slice(1).join(" "));
    }
  }, [isEuropeDriver, me]); // eslint-disable-line

  // Choix manuel de zone sur le formulaire chauffeur (repli si la détection IP/GPS échoue).
  const chooseDriverRegion = async (zone) => {
    const cc = zone === "europe" ? "BE" : "CM";
    try {
      const { data } = await api.get(`/region/config?country_code=${cc}`);
      setRegion(data);
      setVehicleType(zone === "europe" ? "bike" : "moto");
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [me]);
  useEffect(() => {
    try { if ("Notification" in window && Notification.permission === "default") Notification.requestPermission(); } catch { /* ignore */ }
  }, []);
  // PUSH chauffeur : abonner l'appareil pour recevoir les commandes même app en veille + réagir au réveil du Service Worker
  useEffect(() => {
    if (!approved) return;
    if (pushSupported() && notifPermission() === "granted") enablePush().then(() => setNotifOn(true)).catch(() => {});
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (e) => {
      if (e.data && e.data.type === "push") {
        try { playRingtone(); } catch { /* ignore */ }
        api.get("/driver/available-rides").then((r) => setIncoming(r.data || [])).catch(() => {});
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [approved]); // eslint-disable-line
  // Gamification : rang, objectif du jour, progression, temps en ligne
  useEffect(() => {
    if (!approved) return;
    const load = () => api.get("/driver/gamification").then((r) => setGami(r.data)).catch(() => {});
    load();
    const loadBoard = () => api.get("/driver/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
    loadBoard();
    const iv = setInterval(() => { load(); loadBoard(); }, 30000);
    return () => clearInterval(iv);
  }, [approved, available]); // eslint-disable-line
  // Message de la direction (relance interne) — affiché en grand tant qu'il n'est pas lu
  useEffect(() => {
    if (!approved) return;
    api.get("/notifications").then((r) => {
      const items = r.data.items || r.data || [];
      const m = items.find((n) => !n.read && (n.type === "broadcast" || n.type === "info"));
      setAdminMsg(m || null);
    }).catch(() => {});
  }, [approved]); // eslint-disable-line
  const ackAdminMsg = async () => { try { await api.post("/notifications/read-all"); } catch { /* ignore */ } setAdminMsg(null); };
  useEffect(() => { const iv = setInterval(() => setSecTick((s) => s + 1), 1000); return () => clearInterval(iv); }, []);
  // Popup course : compte à rebours 15s → auto-refus (la course est proposée à un autre chauffeur)
  useEffect(() => {
    if (!popupRide) return;
    if (popupLeft <= 0) { decline(popupRide.id); return; }
    const to = setTimeout(() => setPopupLeft((s) => s - 1), 1000);
    return () => clearTimeout(to);
  }, [popupRide, popupLeft]); // eslint-disable-line
  // Ferme le popup si la course n'est plus disponible (prise par un autre / annulée)
  useEffect(() => {
    if (popupRide && !incoming.some((x) => x.id === popupRide.id)) setPopupRide(null);
  }, [incoming, popupRide]);
  // Sonnerie répétée tant que le popup course est affiché (attirer l'attention du chauffeur)
  useEffect(() => {
    if (!popupRide) return;
    const iv = setInterval(playRingtone, 3000);
    return () => clearInterval(iv);
  }, [popupRide]); // eslint-disable-line
  useEffect(() => {
    if (!approved || !available) return;
    const poll = () => api.get("/driver/available-rides").then((r) => {
      const list = r.data || [];
      const prev = prevIncomingRef.current;
      if (prev !== null) {
        const fresh = list.filter((x) => !prev.includes(x.id));
        if (fresh.length) {
          playRingtone();
          toast.success("🛵 Nouvelle course reçue !");
          setPopupRide((cur) => cur || fresh[0]);
          setPopupLeft(15);
          try { if ("Notification" in window && Notification.permission === "granted") new Notification("Mboa Go — Nouvelle course", { body: `${fresh[0].pickup || ""} → ${fresh[0].destination || ""}` }); } catch { /* ignore */ }
        }
      }
      prevIncomingRef.current = list.map((x) => x.id);
      setIncoming(list);
    }).catch(() => {});
    poll();
    const iv = setInterval(poll, 15000);
    return () => clearInterval(iv);
  }, [approved, available]); // eslint-disable-line

  // Live GPS ping is configured below (after the active ride is resolved) for dynamic cadence.

  const toggleAvail = async (v) => {    try {
      let coords = {};
      if (v) {
        try {
          const c = await getCurrentPosition();
          if (c.accuracy != null && c.accuracy > 3000) {
            toast.warning(t("drv.geoImprecise"));  // imprecise (IP/WiFi) — don't broadcast a wrong position
          } else {
            coords = { lat: c.lat, lng: c.lng };
          }
        } catch {
          // GPS refusé/indisponible → repli sur le centre-ville de la zone pour ne pas bloquer
          // (démo, desktop, permission refusée). Le chauffeur peut ensuite s'ajuster ; la position se raffine dès que le GPS répond.
          const cc = region?.country_code || me?.country_code || "CM";
          const center = COUNTRY_CENTERS[cc] || COUNTRY_CENTERS.CM;
          coords = { lat: center.lat, lng: center.lng };
          toast.warning(t("drv.geoOff"));  // GPS indisponible — position approximative (centre-ville)
        }
      }
      await api.put("/driver/availability", { available: v, ...coords });
      setAvailable(v); loadData();
    } catch (e) { toast.error(apiError(e)); }
  };

  const payDeposit = async () => {
    try {
      await api.post("/driver/deposit");
      toast.success("Activation payée ✅ Votre compte est activé.");
      await reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doActivate = async (useCollab = false) => {
    if (useCollab && !actCollab.trim()) return toast.error("Entrez le code collaborateur.");
    if (!useCollab && !actNumber.trim()) return toast.error("Entrez votre numéro Orange Money / MTN MoMo.");
    setActBusy(true); setActDemo("");
    try {
      const body = useCollab ? { collab_code: actCollab.trim() } : { payment_number: actNumber.trim() };
      const { data } = await api.post("/driver/activation/activate", body);
      if (data.pending) { toast.info(data.message); }
      else { toast.success("Compte activé ✅ Vous pouvez passer en ligne."); }
      loadActivation(); await reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setActBusy(false); }
  };

  const doVerify = async () => {
    if (!actCode.trim()) return toast.error("Entrez le code reçu par SMS.");
    setActBusy(true);
    try {
      await api.post("/driver/activation/verify", { code: actCode.trim() });
      toast.success("Compte activé ✅");
      setActCode(""); setActDemo(""); await reload(); loadActivation();
    } catch (e) { toast.error(apiError(e)); }
    finally { setActBusy(false); }
  };

  useEffect(() => {
    if (me?.id) api.get(`/drivers/${me.id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [me?.id]);

  const pickFile = (key) => (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const okType = f.type.startsWith("image/") || f.type === "application/pdf";
    if (!okType) { toast.error(t("drv.docBadType")); e.target.value = ""; return; }
    if (f.size > 5 * 1024 * 1024) { toast.error(t("drv.docTooBig")); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        let url = reader.result;
        if (f.type.startsWith("image/")) {
          const compressed = await compressImage(url);
          if (compressed) url = compressed;
        }
        setDocs((d) => ({ ...d, [key]: url }));
        if (f.type.startsWith("image/")) {
          const q = await analyzeImageQuality(url);
          if (q) {
            if (q.blurry) toast(t("drv.photoBlurry"), { icon: "📷" });
            else if (q.glare) toast(t("drv.photoGlare"), { icon: "✨" });
            else if (q.dark) toast(t("drv.photoDark"), { icon: "🔅" });
          }
        }
      } catch (err) {
        toast.error("Impossible de traiter ce document. Réessayez avec une autre photo ou un fichier plus léger.");
      }
    };
    reader.onerror = () => toast.error("Lecture du fichier impossible. Réessayez.");
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const extractPlate = async () => {
    if (!docs.vehicle_doc?.startsWith("data:image/")) return toast.error(t("drv.plateNeedDoc"));
    setExtracting(true);
    try {
      const { data } = await api.post("/driver/kyc/extract-plate", { vehicle_doc: docs.vehicle_doc });
      if (data.found && data.plate) { setPlate(data.plate); toast.success(t("drv.plateFound")); }
      else toast(t("drv.plateNotFound"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setExtracting(false); }
  };

  const switchService = async (vt) => {
    try {
      await api.post("/driver/active-service", { vehicle_type: vt });
      toast.success("Service activé ✅");
      await reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const addServiceStart = (vt) => { setAddServiceMode(true); setVehicleType(vt); setTab("kyc"); };

  const submitKyc = async () => {
    if (kycSubmitting) return;
    if (!vehicleType) return toast.error(t("drv.vehicleRequired"));
    const isBike = vehicleType === "bike";
    const isCar = vehicleType === "car" || vehicleType === "comfort";
    if (isEuropeDriver && !addServiceMode && (!euPrenom.trim() || !euNom.trim())) return toast.error("Nom et prénom requis.");
    if (isEuropeDriver && !addServiceMode && !iban.trim()) return toast.error("IBAN requis pour vos paiements en € (zone Europe).");
    if (isEuropeDriver && !addServiceMode && !gdprAccepted) return toast.error("Veuillez accepter la politique de confidentialité (RGPD).");
    const docsOptional = !taxiDocsRequired;
    if (isCar && region?.free_period === false) {
      if (!docs.license_doc) return toast.error("Permis de conduire requis pour activer le Transport de Personnes.");
      if (!docs.vehicle_doc) return toast.error("Carte grise requise pour activer le Transport de Personnes.");
      if (!plate.trim()) return toast.error("Plaque d'immatriculation requise pour le Transport de Personnes.");
    }
    if (!docsOptional) {
      if (!docs.id_card_doc || !docs.id_card_back_doc) return toast.error(t("drv.idBothSides"));
      if (!docs.selfie) return toast.error(t("drv.selfieRequired"));
      if (!isBike) {
        if (!docs.vehicle_doc || !plate.trim()) return toast.error(t("drv.submitKyc"));
        if (isCar && !docs.license_doc) return toast.error(t("drv.submitKyc"));
      }
    }
    setKycSubmitting(true);
    try {
      const { data } = await api.post("/driver/kyc", { ...docs, vehicle_plate: isBike ? "" : plate, vehicle_type: vehicleType, add_service: addServiceMode, iban: isEuropeDriver ? iban.trim() : null, address: isEuropeDriver ? euAddress.trim() : null, zone: isEuropeDriver ? "europe" : "africa", vat_number: isEuropeDriver ? vatNumber.trim() : null, gdpr_accepted: isEuropeDriver ? gdprAccepted : null, first_name: isEuropeDriver ? euPrenom.trim() : null, last_name: isEuropeDriver ? euNom.trim() : null });
      if (data?.auto_approved) toast.success(t("drv.kycAutoApproved"));
      else toast.success(t("drv.kycSubmitted"));
      setAddServiceMode(false); reload(); setTab("rides");
    } catch (e) { toast.error(apiError(e)); }
    finally { setKycSubmitting(false); }
  };

  const playRingtone = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      [880, 1175, 880].forEach((f, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
        const t0 = ctx.currentTime + i * 0.18;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.3, t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
        o.start(t0); o.stop(t0 + 0.18);
      });
    } catch { /* ignore */ }
  };
  const decline = async (id) => {
    setPopupRide(null);
    try {
      await api.post(`/rides/${id}/decline`);
      setIncoming((l) => l.filter((r) => r.id !== id));
      prevIncomingRef.current = (prevIncomingRef.current || []).filter((x) => x !== id);
      toast("Course refusée");
    } catch (e) { toast.error(apiError(e)); }
  };
  const markArrived = async (id) => {
    try { await api.post(`/rides/${id}/arrived`); toast.success("Arrivée signalée — attente client démarrée"); loadData(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const noShowCancel = async (id) => {
    try { await api.post(`/rides/${id}/no-show`); toast.success("Course annulée (client absent) et facturée"); loadData(); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const startTrip = async (id) => {
    try { await api.post(`/rides/${id}/start`); toast.success("Course démarrée 🛵"); loadData(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const accept = async (r) => {
    setPopupRide(null);
    const id = typeof r === "string" ? r : r.id;
    // Ouvre Google Maps (navigation) immédiatement dans le geste utilisateur → pas de blocage popup
    if (r && typeof r === "object" && r.pickup_lat != null) {
      try { window.open(googleMapsDirUrl([driverPos, { lat: r.pickup_lat, lng: r.pickup_lng }, { lat: r.dest_lat, lng: r.dest_lng }], true), "_blank"); } catch { /* ignore */ }
    }
    try { await api.post(`/rides/${id}/accept`); toast.success("Course acceptée · navigation Google Maps lancée"); loadData(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const complete = async (id, method) => {
    try { await api.post(`/rides/${id}/complete`, { method }); toast.success(t("pay.received")); setPayMethod(null); setDriverRateRide(id); loadData(); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const startPayment = async (id) => {
    try { await api.post(`/rides/${id}/start-payment`); setPayMethod(null); loadData(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveQr = async (next, nums) => {
    const n = nums || qrNum;
    try { await api.put("/auth/profile", { payment_qr_orange: next.orange, payment_qr_mtn: next.mtn, payment_qr_active: next.active, payment_num_orange: n.orange, payment_num_mtn: n.mtn }); toast.success(t("pay.qrSaved")); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const genQr = async (op) => {
    const num = (qrNum[op] || "").trim();
    if (!num) { toast.error(t("pay.enterNumber")); return; }
    try {
      const dataUrl = await QRCode.toDataURL(num, { width: 320, margin: 1 });
      const next = { ...qr, [op]: dataUrl };
      if (!next.active) next.active = op;
      setQr(next);
      await api.put("/auth/profile", { payment_qr_orange: next.orange, payment_qr_mtn: next.mtn, payment_qr_active: next.active, payment_num_orange: qrNum.orange, payment_num_mtn: qrNum.mtn });
      reload(); toast.success(t("pay.qrGenerated"));
    } catch (e) { toast.error(apiError(e)); }
  };
  const pickQr = (which) => (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error(t("drv.docBadType")); e.target.value = ""; return; }
    if (f.size > 5 * 1024 * 1024) { toast.error(t("drv.docTooBig")); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const next = { ...qr, [which]: reader.result };
      if (!next.active) next.active = which;
      setQr(next); await saveQr(next);
    };
    reader.readAsDataURL(f); e.target.value = "";
  };
  const withdraw = async () => {
    const amt = Number(withdrawAmt);
    if (!amt) return;
    try { await api.post("/driver/withdraw", { amount: amt, operator: "mtn" }); toast.success(t("drv.withdrawOk")); setWithdrawAmt(""); loadData(); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submitDriverRating = async (stars) => {
    if (!driverRateRide) return;
    try { await api.post(`/rides/${driverRateRide}/rate-by-driver`, { rating: stars }); toast.success("Merci pour votre évaluation ⭐"); }
    catch (e) { toast.error(apiError(e)); }
    setDriverRateRide(null);
  };


  const active = myRides.find((r) => r.status === "accepted");
  const [ussdQr, setUssdQr] = useState("");
  useEffect(() => {
    if (payMethod !== "mobile" || !active) { setUssdQr(""); return; }
    const op = qr.active || (qr.orange ? "orange" : "mtn");
    const num = op === "mtn" ? qrNum.mtn : qrNum.orange;
    const u = buildUssd(op, num, active.price);
    if (!u) { setUssdQr(""); return; }
    QRCode.toDataURL(u, { width: 320, margin: 1 }).then(setUssdQr).catch(() => setUssdQr(""));
  }, [payMethod, active?.id, active?.price, qr.active, qr.orange, qr.mtn, qrNum.mtn, qrNum.orange]); // eslint-disable-line

  // Clear the live marker when there is no active ride
  useEffect(() => { if (!active) setDriverPos(null); }, [active?.id]); // eslint-disable-line

  // Live GPS — watchPosition + heartbeat anti-veille tant que le chauffeur est en ligne ; envoi throttlé au backend.
  useEffect(() => {
    if (!approved || !available) return;
    if (!navigator.geolocation) { setGps({ state: "error", msg: "Géolocalisation non supportée par ce navigateur" }); return; }
    let cancelled = false;
    let lastPost = 0;
    const onTrip = !!active;
    const ms = onTrip ? (batterySaver ? 8000 : 3000) : (batterySaver ? 30000 : 15000);
    const postLoc = async (c, force = false) => {
      if (cancelled || !c) return;
      const now = Date.now();
      if (!force && now - lastPost < ms) return;
      lastPost = now;
      try { await api.post("/driver/location", { lat: c.lat, lng: c.lng, accuracy: c.accuracy, status: onTrip ? "en_course" : "en_ligne" }); } catch { /* ignore */ }
    };
    const handle = (pos) => {
      if (cancelled) return;
      const c = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
      if (c.accuracy != null && c.accuracy > 20000) { setGps({ state: "imprecise", acc: Math.round(c.accuracy) }); return; } // pure IP fix — skip
      lastCoordsRef.current = c;
      setDriverPos({ lat: c.lat, lng: c.lng });
      setGps({ state: "ok", acc: Math.round(c.accuracy || 0), at: Date.now() });
      postLoc(c);
    };
    const onErr = (e) => {
      if (cancelled) return;
      const denied = e && e.code === 1;
      setGps({ state: "error", msg: denied ? "Localisation refusée — activez-la pour être visible en direct" : ((e && e.message) || "GPS indisponible") });
      if (denied) toast.error("Localisation refusée. Activez-la dans les réglages de votre navigateur pour apparaître sur la carte en direct.");
    };
    const watchId = navigator.geolocation.watchPosition(handle, onErr, { enableHighAccuracy: true, timeout: 25000, maximumAge: onTrip ? 3000 : 10000 });
    // Heartbeat : garde le statut "en ligne" frais même à l'arrêt (watchPosition ne se déclenche pas sans mouvement).
    const heartbeat = setInterval(() => { if (!cancelled && lastCoordsRef.current) postLoc(lastCoordsRef.current, true); }, Math.min(ms, 45000));
    // Wake Lock : option "garder l'écran allumé" pour éviter la mise en veille pendant le service.
    const acquireWL = async () => {
      if (!wakeOn || wlRef.current) return;
      wlRef.current = await acquireWakeLock();
      try { wlRef.current && wlRef.current.addEventListener && wlRef.current.addEventListener("release", () => { wlRef.current = null; }); } catch { /* ignore */ }
    };
    // Auto-reconnexion : retour au 1er plan ou retour réseau → re-ping + ré-affirme la disponibilité sans action du chauffeur.
    const reconnect = async () => {
      if (cancelled) return;
      await acquireWL();
      const c = lastCoordsRef.current;
      try { await api.put("/driver/availability", { available: true, ...(c ? { lat: c.lat, lng: c.lng } : {}) }); } catch { /* ignore */ }
      if (c) postLoc(c, true);
    };
    const onVis = () => { if (document.visibilityState === "visible") reconnect(); };
    acquireWL();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", reconnect);
    return () => {
      cancelled = true;
      clearInterval(heartbeat);
      try { navigator.geolocation.clearWatch(watchId); } catch { /* ignore */ }
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", reconnect);
      try { wlRef.current && wlRef.current.release && wlRef.current.release(); } catch { /* ignore */ }
      wlRef.current = null;
    };
  }, [approved, available, active?.id, batterySaver, wakeOn]); // eslint-disable-line
  const tabs = [
    { id: "rides", label: t("drv.tabRides"), icon: Bike },
    { id: "earnings", label: t("drv.tabEarnings"), icon: Wallet },
    { id: "receipts", label: t("drv.tabReceipts"), icon: Receipt },
    { id: "kyc", label: t("drv.tabKyc"), icon: FileText },
    { id: "abo", label: t("drv.subTab"), icon: Clock },
    { id: "profile", label: t("drv.tabProfile"), icon: User },
  ];
  const b = kycBadge[status];

  if (user && user.profile_complete === false) {
    return <ProfileSetup onDone={refresh} />;
  }

  return (
    <div className="min-h-screen bg-[#FDF8F0] pb-24">
      <AssistantWidget />
      {approved && available && (
        <div data-testid="driver-gps-status" className={`fixed z-[60] bottom-4 left-4 px-3 py-2 rounded-full text-xs font-semibold shadow-lg border flex items-center gap-2 bg-white ${gps.state === "ok" ? "border-[#16a34a]/40 text-[#16a34a]" : gps.state === "error" ? "border-[#D32F2F]/40 text-[#D32F2F]" : gps.state === "imprecise" ? "border-[#F0C67A] text-[#8A5A00]" : "border-[#E2DFD8] text-[#75807A]"}`}>
          <span className={`w-2 h-2 rounded-full ${gps.state === "ok" ? "bg-[#16a34a] animate-pulse" : gps.state === "error" ? "bg-[#D32F2F]" : gps.state === "imprecise" ? "bg-[#F26419]" : "bg-[#9aa39d]"}`} />
          {gps.state === "ok" ? `GPS actif · ${gps.acc} m` : gps.state === "imprecise" ? "GPS imprécis — active la haute précision" : gps.state === "error" ? "GPS bloqué — autorise la localisation" : "Recherche GPS…"}
        </div>
      )}
      <DashboardSideNav tabs={tabs} active={tab} onSelect={setTab} />
      <div className="lg:pl-72">
      <div className="lg:hidden"><AppHeader variant="app" /></div>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between">
          <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="font-head font-black text-2xl tracking-tight">
            {t("pax.greeting")}, {me?.name || t("hero.driver")}
          </motion.h1>
          {!approved && (
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: `${b.c}20`, color: b.c }}>
              {t(`drv.${b.label}`)}
            </span>
          )}
        </div>

        {approved && !available && wakePending && (
          <button data-testid="driver-wake-banner" onClick={() => toggleAvail(true)} className="mt-4 w-full flex items-center gap-3 text-left bg-[#0b3d2e] text-white rounded-2xl px-4 py-3.5 hover:bg-[#0f4a38] transition-colors">
            <span className="shrink-0 w-9 h-9 rounded-full bg-[#22c55e] flex items-center justify-center animate-pulse"><Bike className="w-5 h-5 text-white" /></span>
            <span className="flex-1 min-w-0">
              <span className="block font-bold text-sm">Une demande vous attend 🛵</span>
              <span className="block text-white/70 text-xs">Passez en ligne maintenant pour la voir et démarrer votre journée.</span>
            </span>
            <span className="shrink-0 text-xs font-bold bg-[#22c55e] rounded-full px-3 py-1.5">Passer en ligne</span>
          </button>
        )}

        {approved && !me?.vehicle_type && (
          <button data-testid="driver-complete-banner" onClick={() => setTab("kyc")} className="mt-4 w-full flex items-center gap-2 text-left bg-[#F26419]/20 border border-[#F26419] text-[#8a6d00] rounded-2xl px-4 py-3 text-sm font-semibold hover:bg-[#F26419]/30 transition-colors">
            <FileText className="w-4 h-4 shrink-0" /> Complétez votre inscription — ajoutez votre véhicule et vos documents (facultatif pour le moment) →
          </button>
        )}

        {/* Availability */}
        {approved && me?.selfie_recheck_due && (
          <button data-testid="selfie-recheck-banner" onClick={() => setShowRecheck(true)} className="mt-4 w-full flex items-center gap-2 text-left bg-[#D32F2F]/15 border border-[#D32F2F] text-[#D32F2F] rounded-2xl px-4 py-3 text-sm font-semibold hover:bg-[#D32F2F]/25 transition-colors">
            <ShieldCheck className="w-4 h-4 shrink-0" /> Contrôle d'identité requis — reprenez un selfie en direct pour reprendre du service →
          </button>
        )}
        {showRecheck && <SelfieRecheck onClose={() => setShowRecheck(false)} onDone={async () => { setShowRecheck(false); await reload(); }} />}
        {approved && Number(me?.wallet || 0) < 0 && (
          <div data-testid="driver-debt-banner" className="mt-5 rounded-3xl p-6 bg-[#D32F2F]/10 border-2 border-[#D32F2F]">
            <div className="flex items-center gap-2 font-head font-black text-lg text-[#D32F2F]"><Wallet className="w-5 h-5" /> Portefeuille en dette</div>
            <p className="mt-2 text-sm text-[#3B423E]">Vous devez régulariser <b data-testid="debt-amount">{fcfa(Math.abs(Math.round(Number(me.wallet))))}</b> de commission (courses payées en espèces) avant de reprendre des courses. Rechargez votre portefeuille pour repasser en ligne.</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button data-testid="debt-recharge-orange" onClick={() => openRecharge("orange_money")} className="h-11 rounded-xl bg-[#FF6600] hover:opacity-90 text-white font-bold inline-flex items-center justify-center gap-2"><Smartphone className="w-4 h-4" /> Orange Money</button>
              <button data-testid="debt-recharge-mtn" onClick={() => openRecharge("mtn_momo")} className="h-11 rounded-xl bg-[#FFCC00] hover:opacity-90 text-[#1A1A1A] font-bold inline-flex items-center justify-center gap-2"><Smartphone className="w-4 h-4" /> MTN MoMo</button>
            </div>
          </div>
        )}
        {approved && !isEuropeDriver && sub?.active && Number(me?.wallet || 0) >= 0 && Number(me?.wallet || 0) < 2000 && (
          <div data-testid="driver-lowbalance-banner" className="mt-5 rounded-3xl p-5 bg-[#FEF3C7] border-2 border-[#F59E0B]">
            <div className="flex items-center gap-2 font-head font-black text-lg text-[#92400E]"><Wallet className="w-5 h-5" /> Solde bas</div>
            <p className="mt-1 text-sm text-[#7c5e12]">Votre solde est de <b data-testid="lowbalance-amount">{fcfa(Math.round(Number(me?.wallet || 0)))}</b>. {sub?.free ? "Pensez à recharger pour provisionner les 19 % de commission prélevés à chaque course. (Pendant la promo, vous continuez à recevoir des courses même à solde bas.)" : "Rechargez maintenant pour couvrir les 19 % de commission et continuer à recevoir des courses."}</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button data-testid="low-recharge-orange" onClick={() => openRecharge("orange_money")} className="h-11 rounded-xl bg-[#FF6600] hover:opacity-90 text-white font-bold inline-flex items-center justify-center gap-2"><Smartphone className="w-4 h-4" /> Orange Money</button>
              <button data-testid="low-recharge-mtn" onClick={() => openRecharge("mtn_momo")} className="h-11 rounded-xl bg-[#FFCC00] hover:opacity-90 text-[#1A1A1A] font-bold inline-flex items-center justify-center gap-2"><Smartphone className="w-4 h-4" /> MTN MoMo</button>
            </div>
          </div>
        )}
        {approved && !isEuropeDriver && sub?.active && (
          <div data-testid="free-launch-banner" className="mt-5 rounded-3xl p-5 bg-gradient-to-br from-[#16a34a] to-[#0f8a3c] text-white flex items-start gap-3 shadow-[0_8px_30px_rgba(22,163,74,0.25)]">
            <Gift className="w-6 h-6 shrink-0 mt-0.5" />
            <div>
              <div className="font-head font-bold text-lg">{sub?.free ? "Entrée gratuite 🎉" : "Compte activé ✓"}</div>
              <p className="text-sm text-white/90 mt-0.5">{sub?.free ? <>Activation <b>offerte</b> jusqu'au 1er janvier 2027 — passez en ligne sans rien payer. </> : "Compte activé à vie — aucun abonnement. "}Commission de <b>19 %</b> prélevée par course.</p>
              {!sub?.free && (
              <button data-testid="activation-receipt-btn" onClick={async () => { try { const r = await api.get('/driver/subscription/receipt.pdf', { responseType: 'blob' }); const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = 'recu-activation.pdf'; a.click(); URL.revokeObjectURL(url); } catch (e) { toast.error("Aucun reçu disponible pour le moment."); } }} className="mt-2 inline-flex items-center gap-2 h-9 px-3 rounded-xl bg-white/15 hover:bg-white/25 text-white text-xs font-semibold transition-colors">📄 Mon reçu d'activation (PDF)</button>
              )}
            </div>
          </div>
        )}
        {approved && isEuropeDriver && (
          <div data-testid="eu-activation-banner" className="mt-5 rounded-3xl p-5 bg-[#1A1A1A] text-white">
            <div className="font-head font-black text-lg">Compte vérifié ✓ · Modèle Uber / Bolt</div>
            <p className="mt-1 text-sm text-white/80">Aucune activation payante en Europe. Vous êtes prêt dès la validation de vos documents. Commission de 10 % par course, prélevée automatiquement — passez en ligne quand vous voulez.</p>
          </div>
        )}
        {approved && !isEuropeDriver && !sub?.active && (
          <div data-testid="activation-card" className="mt-5 rounded-3xl overflow-hidden bg-white border-2 border-[#16a34a]">
            <div className="bg-gradient-to-br from-[#16a34a] to-[#0f8a3c] text-white p-5">
              <div className="font-head font-black text-2xl tracking-tight">Activation unique {fcfa(activation?.fee || 600)}</div>
              <p className="mt-1 text-sm text-white/90 font-semibold">Payez <b>{fcfa(activation?.fee || 600)}</b> une seule fois pour activer votre compte et recevoir des courses — aucun abonnement. Commission de 19 % prélevée en plus, par course.</p>
            </div>
            <div className="p-5">
              <div className="rounded-2xl bg-[#FDF8F0] border border-[#E2DFD8] p-4 flex items-center justify-between">
                <span className="text-sm font-semibold text-[#75807A]">Solde portefeuille</span>
                <span data-testid="act-wallet" className="font-bold text-[#1A1A1A]">{fcfa(activation?.wallet || 0)}</span>
              </div>
              <label className="block mt-4 text-sm font-semibold text-[#1A1A1A]">Code Collaborateur <span className="font-normal text-[#75807A]">(facultatif — activation gratuite)</span></label>
              <div className="flex gap-2 mt-1.5">
                <Input data-testid="act-collab-input" value={actCollab} onChange={(e) => setActCollab(e.target.value.toUpperCase())} placeholder="BSK-COLLAB-001" className="h-12 rounded-xl border-[#E2DFD8] tracking-wide font-semibold" />
                <Button data-testid="act-collab-btn" disabled={actBusy || !actCollab.trim()} onClick={() => doActivate(true)} className="h-12 px-4 rounded-xl bg-[#1A1A1A] hover:bg-black text-white font-bold disabled:opacity-40 shrink-0">Activer gratuitement</Button>
              </div>
              <div className="my-4 flex items-center gap-2 text-xs text-[#75807A]"><span className="h-px flex-1 bg-[#E2DFD8]" />OU ACTIVATION {fcfa(activation?.fee || 600)}<span className="h-px flex-1 bg-[#E2DFD8]" /></div>
              {Number(activation?.wallet || 0) < (activation?.fee || 600) ? (
                <>
                  <p data-testid="act-insufficient" className="text-xs text-[#B8860B]">Solde insuffisant. Rechargez votre portefeuille d'au moins {fcfa(activation?.fee || 600)} pour payer votre activation.</p>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button data-testid="act-recharge-orange" onClick={() => openRecharge("orange_money")} className="h-12 rounded-xl bg-[#FF6600] hover:opacity-90 text-white font-bold inline-flex items-center justify-center gap-2 text-sm"><Smartphone className="w-4 h-4" /> Orange Money</button>
                    <button data-testid="act-recharge-mtn" onClick={() => openRecharge("mtn_momo")} className="h-12 rounded-xl bg-[#FFCC00] hover:opacity-90 text-[#1A1A1A] font-bold inline-flex items-center justify-center gap-2 text-sm"><Smartphone className="w-4 h-4" /> MTN MoMo</button>
                  </div>
                </>
              ) : (
                <Button data-testid="act-pay-btn" disabled={actBusy} onClick={() => doActivate(false)} className="w-full h-12 rounded-xl bg-[#16a34a] hover:bg-[#0f8a3c] text-white font-bold disabled:opacity-50">{actBusy ? "…" : `Payer ${fcfa(activation?.fee || 600)} & activer mon compte`}</Button>
              )}
              <p className="mt-3 text-xs text-center text-[#16a34a] font-semibold">Activation unique, jamais de renouvellement. Commission de 19 % prélevée en plus, par course.</p>
            </div>
          </div>
        )}
        {false && approved && !isEuropeDriver && !me?.deposit_paid && (
          <div data-testid="deposit-card" className="mt-5 rounded-3xl p-6 bg-white border-2 border-[#F26419]">
            <div className="font-head font-bold text-lg text-[#1A1A1A]">Dépôt de garantie requis</div>
            <p className="mt-1 text-sm text-[#75807A]">Activez votre compte chauffeur avec un dépôt unique de <b>1 500 FCFA</b> pour commencer à recevoir des courses.</p>
            <Button data-testid="pay-deposit-btn" onClick={payDeposit} className="mt-4 w-full h-12 rounded-xl bg-[#F26419] hover:bg-[#d9540f] text-white font-bold">Payer le dépôt (1 500 FCFA)</Button>
            <p className="mt-2 text-[11px] text-[#75807A]">Paiement simulé (mode démo).</p>
          </div>
        )}
        {approved && adminMsg && (
          <div data-testid="admin-message-banner" className="mt-4 rounded-3xl border-2 border-[#F26419] bg-[#FFF3EC] p-5">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-xl bg-[#F26419] flex items-center justify-center shrink-0"><BellRing className="w-5 h-5 text-white" /></span>
              <div className="flex-1 min-w-0">
                <div className="font-head font-black text-[#1A1A1A]">{adminMsg.title || "Message Mboa Go"}</div>
                <p className="text-sm text-[#3B423E] mt-1 leading-relaxed">{adminMsg.body}</p>
                <button data-testid="admin-message-ack" onClick={ackAdminMsg} className="mt-3 inline-flex items-center h-10 px-4 rounded-xl bg-[#F26419] hover:bg-[#d9540f] text-white font-bold text-sm transition-colors">J'ai compris ✓</button>
              </div>
            </div>
          </div>
        )}
        {approved && (
          <div className={`mt-5 rounded-3xl p-6 flex items-center justify-between transition-colors ${available ? "bg-[#F26419] text-[#1A1A1A]" : "bg-white border border-[#E2DFD8]"}`}>
            <div>
              <div className="font-head font-bold text-lg">{available ? t("common.online") : t("common.offline")}</div>
              <div className={`text-sm ${available ? "text-[#1A1A1A]/70" : "text-[#75807A]"}`}>{t("drv.available")}</div>
            </div>
            <Switch data-testid="availability-toggle" checked={available} disabled={(!isEuropeDriver && !sub?.active)} onCheckedChange={toggleAvail} />
          </div>
        )}
        {approved && (
          <div data-testid="driver-service-toggles" className="mt-3 grid grid-cols-2 gap-2">
            <button data-testid="notif-toggle" onClick={async () => { try { await enablePush(); setNotifOn(true); toast.success("Notifications activées 🔔"); } catch { toast.error("Autorisez les notifications dans les réglages de votre navigateur."); } }}
              className={`inline-flex items-center justify-center gap-2 h-11 px-3 rounded-xl text-xs font-semibold transition-colors ${notifOn ? "bg-[#16a34a] text-white" : "bg-white border border-[#E2DFD8] text-[#1A1A1A]"}`}>
              <BellRing className="w-4 h-4" /> {notifOn ? "Notifications activées" : "Activer les notifications"}
            </button>
            <button data-testid="wakelock-toggle" onClick={() => { const v = !wakeOn; setWakeOn(v); localStorage.setItem("bsg_wake_on", v ? "1" : "0"); toast.message(v ? "Écran maintenu allumé pendant le service ☀️" : "Écran libéré"); }}
              className={`inline-flex items-center justify-center gap-2 h-11 px-3 rounded-xl text-xs font-semibold transition-colors ${wakeOn ? "bg-[#F26419] text-white" : "bg-white border border-[#E2DFD8] text-[#1A1A1A]"}`}>
              <Sun className="w-4 h-4" /> {wakeOn ? "Écran allumé (service)" : "Garder l'écran allumé"}
            </button>
          </div>
        )}
        {approved && gami && (
          <div data-testid="gamification-card" className="mt-4 rounded-3xl bg-[#1A1A1A] text-white p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-xl bg-[#F26419] flex items-center justify-center"><Trophy className="w-5 h-5 text-white" /></span>
                <div>
                  <div data-testid="gami-rank" className="font-head font-black text-lg leading-none">{gami.rank === "expert" ? "🏆 " : gami.rank === "pro" ? "⭐ " : "🎯 "}{gami.rank_label}</div>
                  <div className="text-[11px] text-white/60 mt-0.5">{gami.rides_30d} courses / 30 j{gami.next_rank ? ` · ${gami.next_rank_remaining} avant ${gami.next_rank}` : " · niveau max"}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] text-white/60">En ligne aujourd'hui</div>
                <div data-testid="gami-online" className="font-head font-bold">{Math.floor((gami.online_seconds_today || 0) / 3600)}h{String(Math.floor(((gami.online_seconds_today || 0) % 3600) / 60)).padStart(2, "0")}</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-white/80">Objectif du jour · {gami.rides_today}/{gami.daily_goal} courses</span>
                {gami.bonus_active && <span data-testid="gami-bonus" className="text-[#4ade80] font-bold">Bonus 0 % actif 🎁</span>}
              </div>
              <div className="h-3 rounded-full bg-white/10 overflow-hidden">
                <div data-testid="gami-progress" className="h-full bg-gradient-to-r from-[#F26419] to-[#16a34a] transition-all duration-500" style={{ width: `${Math.round((gami.progress || 0) * 100)}%` }} />
              </div>
              {!gami.bonus_active ? (
                <div className="text-[11px] text-white/60 mt-2">Encore <b className="text-white">{gami.daily_goal - gami.rides_today}</b> course(s) → toutes vos courses suivantes <b className="text-[#4ade80]">sans commission</b> aujourd'hui 🎁</div>
              ) : (
                <div className="text-[11px] text-[#4ade80] mt-2">🎉 Objectif atteint ! Vos courses au-delà de la 10ᵉ sont 100 % pour vous aujourd'hui.</div>
              )}
              {gami.rank !== "expert" && <div className="text-[11px] text-white/50 mt-1 flex items-center gap-1"><Zap className="w-3 h-3 text-[#F26419]" /> Devenez Expert pour recevoir les courses {gami.expert_priority_seconds}s avant les autres.</div>}
            </div>
          </div>
        )}
        {approved && board && (board.items || []).length > 0 && (
          <div data-testid="leaderboard-card" className="mt-4 rounded-3xl bg-white border border-[#E2DFD8] p-5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-xl bg-[#F26419]/15 flex items-center justify-center"><Trophy className="w-5 h-5 text-[#F26419]" /></span>
                <div>
                  <div className="font-head font-black text-[#1A1A1A] leading-none">Top chauffeurs du jour</div>
                  <div className="text-[11px] text-[#75807A] mt-0.5">Classement par courses terminées aujourd'hui</div>
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              {board.items.map((d) => {
                const medal = d.position === 1 ? "🥇" : d.position === 2 ? "🥈" : d.position === 3 ? "🥉" : null;
                return (
                  <div key={d.driver_id} data-testid={`leaderboard-row-${d.position}`}
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl ${d.is_me ? "bg-[#F26419]/10 border border-[#F26419]/40" : "bg-[#FDF8F0]"}`}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className={`w-7 text-center font-black ${d.position <= 3 ? "text-base" : "text-sm text-[#75807A]"}`}>{medal || d.position}</span>
                      <span className="font-semibold text-sm text-[#1A1A1A] truncate">{d.name}{d.is_me ? " (vous)" : ""}</span>
                    </div>
                    <span className="shrink-0 text-sm font-black text-[#16a34a]">{d.rides} course{d.rides > 1 ? "s" : ""}</span>
                  </div>
                );
              })}
            </div>
            {board.me && !board.items.some((d) => d.is_me) && (
              <div data-testid="leaderboard-me" className="mt-2 flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-[#F26419]/10 border border-[#F26419]/40">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-7 text-center font-black text-sm text-[#75807A]">{board.me.position || "—"}</span>
                  <span className="font-semibold text-sm text-[#1A1A1A] truncate">Vous</span>
                </div>
                <span className="shrink-0 text-sm font-black text-[#16a34a]">{board.me.rides} course{board.me.rides > 1 ? "s" : ""}</span>
              </div>
            )}
            <div className="text-[11px] text-[#75807A] mt-3 text-center">Faites plus de courses pour grimper dans le classement 🚀</div>
          </div>
        )}
        {approved && (
          <div className="mt-4 rounded-3xl overflow-hidden bg-white border border-[#E2DFD8]" data-testid="driver-vehicle-card">
            <div className="h-32 w-full overflow-hidden">
              <img src={VEHICLE_IMG[isEuropeDriver ? "europe" : "africa"][me?.vehicle_type === "car" || me?.vehicle_type === "comfort" ? "car" : me?.vehicle_type === "bike" ? "bike" : "moto"]} alt="vehicle" className="w-full h-full object-cover" />
            </div>
            <div className="p-4 flex items-center gap-3">
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${me?.vehicle_type === "car" ? "bg-[#2563EB]/15" : "bg-[#F26419]/25"}`}>
                {me?.vehicle_type === "car" ? <Car className="w-5 h-5 text-[#2563EB]" /> : <Bike className="w-5 h-5 text-[#F26419]" />}
              </span>
              <div>
                <div className="font-head font-bold text-[#1A1A1A]">{me?.vehicle_type === "car" ? t("delivery.modeCar") : me?.vehicle_type === "bike" ? t("delivery.modeVelo") : t("delivery.modeMoto")}</div>
                <div className="text-xs text-[#75807A]">{me?.vehicle_model || ""}{me?.plate ? ` · ${me.plate}` : ""}</div>
              </div>
            </div>
          </div>
        )}
        {approved && (
          <div className="mt-4 rounded-3xl bg-white border border-[#E2DFD8] p-5" data-testid="service-switcher">
            <div className="font-head font-bold text-[#1A1A1A] mb-1">{isEuropeDriver ? "Mon service" : "Mon véhicule"}</div>
            <p className="text-xs text-[#75807A] mb-3">Basculez entre vos services. Un nouveau service nécessite la validation de vos documents.</p>
            <div className={`grid gap-3 ${serviceOptions.length > 2 ? "grid-cols-3" : "grid-cols-2"}`}>
              {serviceOptions.map((s) => {
                const activeSvc = me?.vehicle_type === s.vt;
                const owned = (me?.approved_services || []).includes(s.vt);
                return (
                  <button key={s.vt} data-testid={`service-opt-${s.vt}`}
                    onClick={() => activeSvc ? null : owned ? switchService(s.vt) : addServiceStart(s.vt)}
                    className={`relative h-20 rounded-2xl border-2 flex flex-col items-center justify-center gap-1 font-semibold text-xs text-center px-1 transition-all ${activeSvc ? "border-[#F26419] bg-[#F26419]/10" : "border-[#E2DFD8] hover:border-[#F26419]"}`}>
                    <s.Icon className={`w-5 h-5 ${activeSvc ? "text-[#F26419]" : "text-[#75807A]"}`} />
                    {s.label}
                    {activeSvc && <span data-testid={`service-active-${s.vt}`} className="absolute top-1.5 right-2 text-[9px] font-bold text-[#F26419] uppercase">Actif</span>}
                    {!owned && !activeSvc && <span data-testid={`service-add-${s.vt}`} className="absolute top-1.5 right-2 text-[9px] font-bold text-[#2563EB] uppercase">+ Ajouter</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {approved && reviews && reviews.count > 0 && (
          <div data-testid="driver-reviews-card" className="mt-4 rounded-3xl bg-white border border-[#E2DFD8] p-5">
            <div className="flex items-center justify-between">
              <div className="font-head font-bold text-[#1A1A1A]">Mes avis</div>
              <div className="flex items-center gap-1.5 text-sm font-bold text-[#1A1A1A]">
                <Star className="w-4 h-4 text-[#F26419] fill-[#F26419]" /> {Number(reviews.avg).toFixed(1)}
                <span className="text-xs font-normal text-[#75807A]">· {reviews.count} course{reviews.count > 1 ? "s" : ""} notée{reviews.count > 1 ? "s" : ""}</span>
              </div>
            </div>
            {reviews.reviews.length > 0 ? (
              <div className="mt-3 space-y-2.5" data-testid="driver-reviews-list">
                {reviews.reviews.map((rv, i) => (
                  <div key={i} className="rounded-2xl bg-[#FDF8F0] border border-[#E2DFD8] p-3">
                    <div className="flex items-center gap-1 text-[#F26419]">
                      {[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-3.5 h-3.5 ${s <= rv.rating ? "fill-[#F26419]" : "fill-transparent text-[#E2DFD8]"}`} />)}
                    </div>
                    <p className="mt-1.5 text-sm text-[#3B423E]">« {rv.comment} »</p>
                    <p className="mt-1 text-[11px] text-[#75807A]">— {rv.passenger_name}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-xs text-[#75807A]">Aucun commentaire pour le moment.</p>
            )}
          </div>
        )}
        {!approved && status !== "not_submitted" && (
          <div className="mt-5 rounded-3xl bg-white border border-[#E2DFD8] p-6 flex items-center gap-3">
            <Clock className="w-6 h-6 text-[#F26419]" />
            <p className="text-sm text-[#3B423E]">{status === "rejected" ? t("drv.rejected") : t("drv.pending")}</p>
          </div>
        )}

        {driverRateRide && (
          <div className="fixed inset-0 z-[2000] bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="driver-rating-modal">
            <div className="bg-white rounded-3xl p-6 w-full max-w-sm text-center">
              <h3 className="font-head font-black text-xl text-[#1A1A1A]">Notez la course</h3>
              <p className="text-sm text-[#75807A] mt-1">Comment s'est passé le trajet avec le passager ?</p>
              <div className="flex justify-center gap-2 mt-5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} type="button" data-testid={`driver-star-${s}`} onClick={() => submitDriverRating(s)} className="p-1 hover:scale-110 transition-transform">
                    <Star className="w-9 h-9 text-[#F26419] fill-[#F26419]/20" />
                  </button>
                ))}
              </div>
              <button type="button" data-testid="driver-rating-skip" onClick={() => setDriverRateRide(null)} className="mt-5 text-sm text-[#75807A] hover:text-[#1A1A1A]">Passer</button>
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {tab === "rides" && (            <motion.div key="rides" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6 space-y-3">
              {false && approved && sub && !sub.free && !sub.active && !sub.suspended && (
                <div data-testid="renew-banner" className="rounded-3xl bg-[#D32F2F]/10 border-2 border-[#D32F2F] p-5">
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 shrink-0 rounded-xl bg-[#D32F2F] text-white flex items-center justify-center"><Clock className="w-5 h-5" /></span>
                    <div className="flex-1">
                      <div className="font-head font-bold text-[#D32F2F]">{t("drv.renewTitle")}</div>
                      <p className="text-sm text-[#3B423E]">{t("drv.renewText")}</p>
                    </div>
                  </div>
                  <button data-testid="renew-banner-btn" onClick={renewSub} className="mt-4 w-full h-12 rounded-2xl bg-[#F26419] hover:bg-[#D85312] text-[#1A1A1A] font-black flex items-center justify-center gap-2"><Clock className="w-5 h-5" /> {t("drv.subRenew")}</button>
                </div>
              )}
              {!approved && (
                <div className="rounded-2xl bg-[#F0EBE1] p-6 text-center">
                  <FileText className="w-8 h-8 mx-auto text-[#75807A]" />
                  <p className="mt-2 text-sm text-[#3B423E]">{status === "not_submitted" ? t("drv.notSubmitted") : t("drv.pending")}</p>
                  {status === "not_submitted" && <Button data-testid="goto-kyc" onClick={() => setTab("kyc")} className="mt-4 h-12 rounded-xl bg-[#F26419] text-white">{t("drv.submitKyc")}</Button>}
                </div>
              )}
              {approved && active && (
                <div className="rounded-3xl bg-[#1A1A1A] text-white p-6" data-testid="active-ride">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wide text-[#D94F2B] font-semibold">{t("drv.inProgress")}</div>
                    <span data-testid="active-type-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-white/10">{active.type === "delivery" ? <><Package className="w-3 h-3" /> {t("delivery.badge")}</> : active.vehicle_type === "car" ? <><Car className="w-3 h-3" /> {t("delivery.badgeCar")}</> : <><Bike className="w-3 h-3" /> {t("delivery.badgeMoto")}</>}</span>
                  </div>
                  {active.pickup_lat != null && active.dest_lat != null && (
                    <div className="mt-4">
                      <RideMap
                        center={driverPos || { lat: active.pickup_lat, lng: active.pickup_lng }}
                        pickup={{ lat: active.pickup_lat, lng: active.pickup_lng }}
                        dest={{ lat: active.dest_lat, lng: active.dest_lng }}
                        driver={driverPos}
                        height={200}
                      />
                      <div className="mt-2 flex items-center gap-2 text-xs text-white/70">
                        <span className="w-2 h-2 rounded-full bg-[#F26419] animate-pulse" /> {t("drv.liveTracking")} · {t("drv.enRoute")}
                      </div>
                      <button data-testid="battery-saver-toggle" onClick={() => { const v = !batterySaver; setBatterySaver(v); localStorage.setItem("bsg_battery_saver", v ? "1" : "0"); }} className={`mt-2 inline-flex items-center gap-2 h-9 px-3 rounded-xl text-xs font-semibold transition-colors ${batterySaver ? "bg-[#16a34a] text-white" : "bg-white/10 text-white/80"}`}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="6" width="18" height="12" rx="2" /><line x1="23" y1="10" x2="23" y2="14" /></svg>
                        Mode Économie Batterie {batterySaver ? "activé" : "désactivé"}
                      </button>
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#F26419]" />{active.pickup}</div>
                  <div className="mt-1 flex items-center gap-2"><MapPin className="w-3 h-3 text-[#D94F2B]" />{active.destination}</div>
                  {active.passenger_id && !active.payment_pending && (
                    <CallButton testId="driver-call-client-btn" targetUserId={active.passenger_id} name={active.passenger_name || "Client"} label="Appeler le client" className="mt-3 w-full h-11 rounded-xl bg-[#16a34a] hover:bg-[#128a3e] text-white font-bold text-sm inline-flex items-center justify-center gap-1.5" />
                  )}
                  {active.type === "delivery" && (
                    <div className="mt-2 text-xs text-white/70" data-testid="active-delivery-info"><Package className="w-3 h-3 inline mr-1 text-[#D94F2B]" />{active.item_description} · {active.recipient_name} {active.recipient_phone}</div>
                  )}
                  {!active.payment_pending ? (
                  <>
                  {active.pickup_lat != null && (
                    <a data-testid="gmaps-driver" href={googleMapsDirUrl([driverPos, { lat: active.pickup_lat, lng: active.pickup_lng }, { lat: active.dest_lat, lng: active.dest_lng }], true)} target="_blank" rel="noreferrer" className="mb-2 w-full h-11 rounded-xl bg-[#1A73E8] hover:bg-[#1666d0] text-white font-bold text-sm flex items-center justify-center gap-2">🧭 Navigation Google Maps</a>
                  )}
                  {!active.arrived_at ? (
                    <button data-testid="arrived-btn" onClick={() => markArrived(active.id)} className="mt-4 w-full h-11 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm">Je suis arrivé au rendez-vous</button>
                  ) : (() => {
                    const left = Math.max(0, 300 - Math.floor((Date.now() - new Date(active.arrived_at).getTime()) / 1000));
                    return left > 0
                      ? <div data-testid="wait-timer" className="mt-4 rounded-xl bg-white/10 p-3 text-center text-sm">⏳ Attente client : {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")} — annulation facturée possible ensuite</div>
                      : <button data-testid="no-show-btn" onClick={() => noShowCancel(active.id)} className="mt-4 w-full h-11 rounded-xl bg-[#D32F2F] hover:bg-[#b71c1c] text-white font-bold text-sm">Client absent — Annuler & facturer ({fcfa(500)})</button>;
                  })()}
                  {active.arrived_at && !active.trip_started && (
                    <button data-testid="start-trip-btn" onClick={() => startTrip(active.id)} className="mt-3 w-full h-11 rounded-xl bg-[#16a34a] hover:bg-[#128a3e] text-white font-bold text-sm">Démarrer la course</button>
                  )}
                  {active.trip_started && (
                    <div data-testid="trip-inprogress" className="mt-3 rounded-xl bg-[#16a34a]/20 border border-[#16a34a]/40 p-2 text-center text-sm text-[#7CE0A3]">⏱ Course en cours</div>
                  )}
                  <div className="mt-4 flex items-center gap-3">
                    <div className="font-head font-black text-2xl flex-1">{money(active.price, active.currency)}</div>
                    <a data-testid="whatsapp-driver" href={waLink(active.passenger_phone, t("common.waHello"))} target="_blank" rel="noopener noreferrer" className="h-12 w-12 rounded-xl bg-[#25D366] hover:opacity-90 flex items-center justify-center text-white" title={t("common.whatsapp")}>
                      <MessageCircle className="w-5 h-5" />
                    </a>
                    <button data-testid="chat-open-driver" onClick={() => setChatOpen(true)} className="h-12 w-12 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center">
                      <MessageCircle className="w-5 h-5" />
                    </button>
                    <Button data-testid="complete-ride" onClick={() => startPayment(active.id)} className="h-12 px-6 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold">{t("drv.complete")}</Button>
                  </div>
                  </>
                  ) : isEuropeDriver ? (
                  <div className="mt-4 rounded-2xl bg-white text-[#1A1A1A] p-4" data-testid="driver-payment-panel">
                    <div className="text-center">
                      <div className="text-xs text-[#75807A]">Montant de la course</div>
                      <div className="font-head font-black text-3xl">{money(active.price, active.currency)}</div>
                    </div>
                    <div className="mt-3 text-center text-sm text-[#16a34a] font-semibold" data-testid="eu-card-paid">Payé par carte bancaire · rien à encaisser</div>
                    <Button data-testid="payment-received-btn" onClick={() => complete(active.id, "stripe")} className="mt-4 w-full h-14 rounded-xl bg-[#16a34a] hover:bg-[#128a3e] text-white font-bold text-base">Terminer la course</Button>
                  </div>
                  ) : (
                  <div className="mt-4 rounded-2xl bg-white text-[#1A1A1A] p-4" data-testid="driver-payment-panel">
                    <div className="text-center text-[11px] font-bold uppercase tracking-wide text-[#16a34a] bg-[#16a34a]/10 rounded-lg py-1.5 mb-3">{t("pay.freeBanner")}</div>
                    <div className="text-center">
                      <div className="text-xs text-[#75807A]">{t("pay.amountDue")}</div>
                      <div className="font-head font-black text-3xl">{money(active.price, active.currency)}</div>
                    </div>
                    {active.client_marked_paid && <div className="mt-2 text-center text-xs font-semibold text-[#16a34a]" data-testid="client-paid-flag">{t("pay.clientPaidFlag")}</div>}
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <button data-testid="pay-cash-btn" onClick={() => setPayMethod("cash")} className={`h-14 rounded-xl border-2 font-bold text-sm flex items-center justify-center gap-2 transition-all ${payMethod === "cash" ? "border-[#D94F2B] bg-[#FFF3E9] text-[#D94F2B]" : "border-[#E2DFD8] text-[#1A1A1A]"}`}><Banknote className="w-5 h-5" /> {t("pay.cash")}</button>
                      <button data-testid="pay-mobile-btn" onClick={() => setPayMethod("mobile")} className={`h-14 rounded-xl border-2 font-bold text-sm flex items-center justify-center gap-2 transition-all ${payMethod === "mobile" ? "border-[#D94F2B] bg-[#FFF3E9] text-[#D94F2B]" : "border-[#E2DFD8] text-[#1A1A1A]"}`}><Smartphone className="w-5 h-5" /> {t("pay.mobile")}</button>
                    </div>
                    {payMethod === "cash" && <div className="mt-3 text-center text-sm text-[#75807A]" data-testid="cash-hint">{t("pay.cashHint")}</div>}
                    {payMethod === "mobile" && (
                      <div className="mt-3" data-testid="driver-qr">
                        {(qr.orange || qr.mtn) ? (
                          <>
                            {qr.orange && qr.mtn && (
                              <div className="flex justify-center gap-2 mb-2">
                                <button data-testid="qr-toggle-orange" onClick={() => setQr((q) => ({ ...q, active: "orange" }))} className={`px-3 h-8 rounded-lg text-xs font-bold ${(qr.active || "orange") === "orange" ? "bg-[#FF6600] text-white" : "bg-[#EFEBE4] text-[#3B423E]"}`}>Orange</button>
                                <button data-testid="qr-toggle-mtn" onClick={() => setQr((q) => ({ ...q, active: "mtn" }))} className={`px-3 h-8 rounded-lg text-xs font-bold ${qr.active === "mtn" ? "bg-[#FFCC00] text-black" : "bg-[#EFEBE4] text-[#3B423E]"}`}>MTN</button>
                              </div>
                            )}
                            <img src={ussdQr || (((qr.active || (qr.orange ? "orange" : "mtn")) === "mtn") ? qr.mtn : qr.orange)} alt="QR" className="w-44 h-44 object-contain mx-auto rounded-xl border border-[#E2DFD8] bg-white" />
                            <div className="mt-1 text-center text-xs text-[#75807A]">{ussdQr ? `${t("pay.scanUssd")} ${fcfa(active.price)}` : t("pay.showQrToClient")}</div>
                          </>
                        ) : (
                          <div className="text-center text-sm text-[#D32F2F]" data-testid="no-qr-hint">{t("pay.noQr")}</div>
                        )}
                      </div>
                    )}
                    <Button data-testid="payment-received-btn" disabled={!payMethod} onClick={() => complete(active.id, payMethod)} className="mt-4 w-full h-14 rounded-xl bg-[#16a34a] hover:bg-[#128a3e] text-white font-bold text-base disabled:opacity-40">{t("pay.received")}</Button>
                  </div>
                  )}
                </div>
              )}
              {approved && !active && (
                <>
                  <h2 className="font-head font-bold text-lg">{t("drv.incoming")}</h2>
                  {incoming.length === 0 && <p className="text-[#75807A] py-8 text-center text-sm">{available ? t("drv.noIncoming") : t("common.offline")}</p>}
                  {incoming.map((r) => (
                    <div key={r.id} data-testid={`incoming-${r.id}`} className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
                      <div className="flex justify-between items-start">
                        <div>
                          <span data-testid={`incoming-type-${r.id}`} className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full mb-2 ${r.type === "delivery" ? "bg-[#F26419]/15 text-[#D94F2B]" : "bg-[#1A1A1A]/10 text-[#1A1A1A]"}`}>{r.type === "delivery" ? <><Package className="w-3 h-3" /> {t("delivery.badge")}</> : r.vehicle_type === "car" ? <><Car className="w-3 h-3" /> {t("delivery.badgeCar")}</> : <><Bike className="w-3 h-3" /> {t("delivery.badgeMoto")}</>}</span>
                          <div className="flex items-center gap-2 text-sm"><span className="w-2 h-2 rounded-full bg-[#F26419]" />{r.pickup}</div>
                          <div className="flex items-center gap-2 text-sm mt-1"><MapPin className="w-3 h-3 text-[#D94F2B]" />{r.destination}</div>
                          {r.type === "delivery" && (
                            <div className="mt-1 text-xs text-[#3B423E]" data-testid={`incoming-delivery-${r.id}`}><Package className="w-3 h-3 inline mr-1 text-[#D94F2B]" />{r.item_description || "—"} · {r.recipient_name || ""} {r.recipient_phone || ""}</div>
                          )}
                          <div className="mt-2 text-xs text-[#75807A]">{r.distance_km} km · {t(`pay.${r.payment_method}`)}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-head font-bold text-lg">{money(r.price, r.currency)}</div>
                          <div className="mt-2 flex flex-col gap-2 items-end w-28">
                            <Button data-testid={`accept-${r.id}`} onClick={() => accept(r)} className="h-10 px-4 rounded-xl bg-[#16a34a] hover:bg-[#128a3e] text-white text-sm font-bold w-full">Accepter</Button>
                            <button data-testid={`decline-${r.id}`} onClick={() => decline(r.id)} className="h-9 px-4 rounded-xl border border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F]/5 text-sm font-semibold w-full">Refuser</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </motion.div>
          )}

          {tab === "earnings" && (
            <motion.div key="earn" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6 space-y-4">
              {!isEuropeDriver && (
              <div className="bg-[#F26419] rounded-3xl p-7 text-white">
                <div className="text-white/70 text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4" /> {t("drv.net")}</div>
                <div className="font-head font-black text-4xl mt-1" data-testid="net-earnings">{fcfa(earnings?.net_earnings || 0)}</div>
                <div className="mt-2 text-xs text-white/80" data-testid="commission-line">{t("drv.brut")}: {fcfa(earnings?.total_earnings || 0)} · {t("drv.commission")} 19% : -{fcfa(earnings?.commission || 0)}</div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="bg-white/10 rounded-xl p-3"><div className="text-xs text-white/70">{t("drv.momo")}</div><div className="font-bold">{fcfa(earnings?.momo_earnings || 0)}</div></div>
                  <div className="bg-white/10 rounded-xl p-3"><div className="text-xs text-white/70">{t("drv.cash")}</div><div className="font-bold">{fcfa(earnings?.cash_earnings || 0)}</div></div>
                </div>
              </div>
              )}
              {(isEuropeDriver || earnings?.eur_rides_count > 0) && (
                <div className="bg-[#EC407A] rounded-3xl p-7 text-white" data-testid="eur-earnings-card">
                  <div className="text-white/80 text-sm flex items-center gap-2">🇪🇺 <TrendingUp className="w-4 h-4" /> Gains Europe (€)</div>
                  <div className="font-head font-black text-4xl mt-1" data-testid="eur-net-earnings">{(earnings.eur_net || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
                  <div className="mt-2 text-xs text-white/85" data-testid="eur-commission-line">
                    Brut : {(earnings.eur_total || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € · Commission {Math.round((earnings.eur_commission_rate || 0.1) * 100)}% : -{(earnings.eur_commission || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €
                  </div>
                  <div className="mt-4 bg-white/10 rounded-xl p-3 text-xs text-white/80">{earnings.eur_rides_count} course(s) payée(s) par carte (Stripe) · versées sur votre IBAN</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl border border-[#E2DFD8] p-5"><div className="text-xs text-[#75807A]">{t("drv.ridesCount")}</div><div className="font-head font-black text-2xl">{earnings?.rides_count || 0}</div></div>
                <div className="bg-white rounded-2xl border border-[#E2DFD8] p-5"><div className="text-xs text-[#75807A]">{t("drv.rating")}</div><div className="font-head font-black text-2xl flex items-center gap-1">{(earnings?.rating || 0).toFixed(1)}<Star className="w-4 h-4 fill-[#F26419] text-[#F26419]" /></div></div>
              </div>
              <div className="bg-white rounded-3xl border border-[#E2DFD8] p-6">
                <div className="flex items-center gap-2"><Wallet className="w-5 h-5 text-[#D94F2B]" /><h3 className="font-head font-semibold">{t("drv.wallet")}</h3></div>
                <div className="font-head font-black text-3xl mt-2" data-testid="wallet-balance">{money(earnings?.wallet || 0)}</div>
                {!isEuropeDriver && (
                  <>
                    <p className="mt-2 text-xs text-[#75807A]">Rechargez votre portefeuille via Orange Money ou MTN MoMo. <span className="font-semibold text-[#D94F2B]">Chaque recharge est validée manuellement par l'administrateur</span> après vérification du paiement.</p>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <button data-testid="driver-recharge-orange" onClick={() => openRecharge("orange_money")} className="h-11 rounded-xl bg-[#FF6600] hover:opacity-90 text-white font-bold inline-flex items-center justify-center gap-2"><Smartphone className="w-4 h-4" /> Orange Money</button>
                      <button data-testid="driver-recharge-mtn" onClick={() => openRecharge("mtn_momo")} className="h-11 rounded-xl bg-[#FFCC00] hover:opacity-90 text-[#1A1A1A] font-bold inline-flex items-center justify-center gap-2"><Smartphone className="w-4 h-4" /> MTN MoMo Pay</button>
                    </div>
                  </>
                )}
                <div className="mt-4 flex gap-3">
                  <Input data-testid="withdraw-amount" value={withdrawAmt} onChange={(e) => setWithdrawAmt(e.target.value)} type="number" placeholder={t("drv.withdrawAmount")} className="h-12 rounded-xl border-2" />
                  <Button data-testid="withdraw-btn" onClick={withdraw} className="h-12 px-5 rounded-xl bg-[#F26419] text-white font-bold whitespace-nowrap"><Banknote className="w-4 h-4 mr-1" />{t("drv.withdraw")}</Button>
                </div>
              </div>
            </motion.div>
          )}

          {tab === "receipts" && (
            <motion.div key="receipts" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6">
              <div className="bg-white rounded-3xl border border-[#E2DFD8] p-6">
                <div className="flex items-center gap-2"><Receipt className="w-5 h-5 text-[#D94F2B]" /><h2 className="font-head font-bold text-lg">{t("drv.receipts")}</h2></div>
                <div className="mt-4 flex flex-wrap gap-2" data-testid="receipts-period-filter">
                  {["day", "week", "month", "all"].map((p) => (
                    <button key={p} data-testid={`receipts-period-${p}`} onClick={() => { setReceiptsPeriod(p); api.get(`/driver/receipts?period=${p}`).then((r) => { setReceipts(r.data.items || []); setReceiptsTotal(r.data.total || 0); setReceiptsCash(r.data.total_cash || 0); setReceiptsMomo(r.data.total_momo || 0); }).catch(() => {}); }} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${receiptsPeriod === p ? "bg-[#F26419] text-white" : "bg-[#FDF8F0] text-[#75807A] border border-[#E2DFD8]"}`}>{t(`drv.period_${p}`)}</button>
                  ))}
                </div>
                <div data-testid="receipts-daterange" className="mt-3 flex flex-wrap items-end gap-2">
                  <div><label className="text-[11px] text-[#75807A]">{t("drv.from")}</label><input data-testid="receipts-from" type="date" value={receiptsFrom} onChange={(e) => setReceiptsFrom(e.target.value)} className="block h-9 rounded-lg border border-[#E2DFD8] px-2 text-sm" /></div>
                  <div><label className="text-[11px] text-[#75807A]">{t("drv.to")}</label><input data-testid="receipts-to" type="date" value={receiptsTo} onChange={(e) => setReceiptsTo(e.target.value)} className="block h-9 rounded-lg border border-[#E2DFD8] px-2 text-sm" /></div>
                  <button data-testid="receipts-apply" onClick={() => { setReceiptsPeriod("custom"); api.get(`/driver/receipts?start_date=${receiptsFrom}&end_date=${receiptsTo}`).then((r) => { setReceipts(r.data.items || []); setReceiptsTotal(r.data.total || 0); setReceiptsCash(r.data.total_cash || 0); setReceiptsMomo(r.data.total_momo || 0); }).catch(() => {}); }} className="h-9 px-4 rounded-lg bg-[#1A1A1A] text-white text-sm font-semibold">{t("drv.apply")}</button>
                </div>
                <div data-testid="receipts-total" className="mt-4 rounded-2xl bg-[#1A1A1A] text-white p-4 flex items-center justify-between">
                  <span className="text-sm text-white/70">{t("drv.receiptsTotal")}</span>
                  <span className="font-head font-black text-2xl">{money(receiptsTotal)}</span>
                </div>
                {!isEuropeDriver && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div data-testid="receipts-cash" className="rounded-xl bg-[#16a34a]/10 p-3"><div className="text-[#16a34a] font-head font-bold">{fcfa(receiptsCash)}</div><div className="text-xs text-[#75807A]">{t("drv.cash")}</div></div>
                  <div data-testid="receipts-momo" className="rounded-xl bg-[#F26419]/10 p-3"><div className="text-[#D94F2B] font-head font-bold">{fcfa(receiptsMomo)}</div><div className="text-xs text-[#75807A]">{t("drv.momo")}</div></div>
                </div>
                )}
                <div className="mt-4 space-y-3" data-testid="driver-receipts">
                  {receipts.length === 0 && <p className="text-sm text-[#75807A] text-center py-8">{t("drv.receiptsEmpty")}</p>}
                  {receipts.map((r) => (
                    <div key={r.id} data-testid={`receipt-${r.id}`} className="flex items-center justify-between border-b border-[#F0EBE1] pb-3">
                      <div className="min-w-0 pr-3">
                        <div className="text-sm font-medium truncate">{r.pickup} → {r.destination}</div>
                        <div className="text-xs text-[#75807A]">{r.date ? new Date(r.date).toLocaleString("fr-FR") : "—"}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-head font-bold">{money(r.amount || 0, r.currency)}</div>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${isEuropeDriver ? "bg-[#2563EB]/15 text-[#2563EB]" : r.method === "cash" ? "bg-[#16a34a]/15 text-[#16a34a]" : "bg-[#F26419]/15 text-[#D94F2B]"}`}>{isEuropeDriver ? "Carte" : r.method === "cash" ? t("drv.cash") : t("drv.momo")}</span>
                        <button data-testid={`receipt-pdf-${r.id}`} onClick={() => downloadReceipt(r.id)} className="mt-1 block ml-auto text-[11px] font-semibold text-[#1A1A1A] underline hover:text-[#D94F2B]">{t("drv.pdf")}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {tab === "kyc" && (
            <motion.div key="kyc" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6">
              <div className="bg-white rounded-3xl border border-[#E2DFD8] p-6">
                <h2 className="font-head font-bold text-lg">{t("drv.kyc")}</h2>
                {addServiceMode && (
                  <div data-testid="add-service-banner" className="mt-3 rounded-2xl border-2 border-[#2563EB] bg-[#2563EB]/5 p-4 flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-[#1A1A1A]">Ajout d'un nouveau service — soumettez les documents requis pour l'activer.</div>
                    <button type="button" data-testid="add-service-cancel" onClick={() => { setAddServiceMode(false); setTab("rides"); }} className="text-xs font-bold text-[#2563EB] underline shrink-0">Annuler</button>
                  </div>
                )}
                {!approved && (
                  <div className="mt-3 grid grid-cols-2 gap-2" data-testid="driver-region-toggle">
                    <button type="button" data-testid="driver-region-africa" onClick={() => chooseDriverRegion("africa")} className={`h-11 rounded-xl border-2 text-sm font-semibold transition-colors ${!isEuropeDriver ? "border-[#F26419] bg-[#F26419]/10" : "border-[#E2DFD8] hover:border-[#F26419]"}`}>🇨🇲 Cameroun</button>
                    <button type="button" data-testid="driver-region-europe" onClick={() => chooseDriverRegion("europe")} className={`h-11 rounded-xl border-2 text-sm font-semibold transition-colors ${isEuropeDriver ? "border-[#2563EB] bg-[#2563EB]/10" : "border-[#E2DFD8] hover:border-[#2563EB]"}`}>🇧🇪 Europe (Bruxelles)</button>
                  </div>
                )}
                {isEuropeDriver && !approved && (
                  <div data-testid="eu-driver-banner" className="mt-3 rounded-2xl border-2 border-[#2563EB] bg-[#2563EB]/5 p-4">
                    <div className="font-head font-bold text-[#1A1A1A] flex items-center gap-2">🇧🇪 Inscription chauffeur · Chauffeurregistratie · Driver registration</div>
                    <p className="text-xs text-[#75807A] mt-1">Bruxelles / Europe — Voiture ou Vélo · Auto of Fiets · Car or Bike. Paiements en € (IBAN).</p>
                  </div>
                )}
                {isEuropeDriver && !approved && (
                  <div data-testid="eu-identity" className="mt-3 bg-white rounded-2xl border-2 border-[#E2DFD8] p-4 space-y-3">
                    <div className="text-xs font-bold uppercase tracking-wide text-[#75807A]">Identité · Identiteit · Identity</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium text-[#3B423E]">Prénom <span className="text-[#75807A] font-normal">· Voornaam · First name</span></label>
                        <Input data-testid="kyc-firstname" value={euPrenom} onChange={(e) => setEuPrenom(e.target.value)} placeholder="Jean" className="mt-1.5 h-13 rounded-xl border-2" />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#3B423E]">Nom <span className="text-[#75807A] font-normal">· Naam · Last name</span></label>
                        <Input data-testid="kyc-lastname" value={euNom} onChange={(e) => setEuNom(e.target.value)} placeholder="Dupont" className="mt-1.5 h-13 rounded-xl border-2" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#3B423E]">Téléphone <span className="text-[#75807A] font-normal">· Telefoon · Phone</span></label>
                      <Input data-testid="kyc-phone" value={me?.phone || ""} disabled className="mt-1.5 h-13 rounded-xl border-2 bg-[#F6F3ED] text-[#75807A]" />
                    </div>
                  </div>
                )}
                {approved && !addServiceMode ? (
                  <div className="mt-4 flex items-center gap-2 text-[#D94F2B]"><Check className="w-5 h-5" /> {t("coverage.live")}</div>
                ) : (
                  <div className="mt-5 space-y-4">
                    <div data-testid="kyc-vehicle-type">
                      <label className="text-sm font-medium text-[#3B423E]">{t("drv.vehicleType")}</label>
                      <div className={`mt-1.5 grid gap-3 ${isEuropeDriver ? "grid-cols-2" : "grid-cols-3"}`}>
                        {!isEuropeDriver && (
                          <button type="button" data-testid="kyc-vehicle-moto" onClick={() => setVehicleType("moto")} className={`h-16 rounded-xl border-2 flex flex-col items-center justify-center gap-1 font-semibold text-sm transition-all ${vehicleType === "moto" ? "border-[#F26419] bg-[#F26419]/10" : "border-[#E2DFD8]"}`}>
                            <Bike className="w-5 h-5 text-[#F26419]" /> {t("delivery.modeMoto")}
                          </button>
                        )}
                        <button type="button" data-testid="kyc-vehicle-car" onClick={() => setVehicleType("car")} className={`h-16 rounded-xl border-2 flex flex-col items-center justify-center gap-1 font-semibold text-sm transition-all ${vehicleType === "car" ? "border-[#2563EB] bg-[#2563EB]/10" : "border-[#E2DFD8]"}`}>
                          <Car className="w-5 h-5 text-[#2563EB]" /> {t("delivery.modeCar")}
                        </button>
                        <button type="button" data-testid="kyc-vehicle-bike" onClick={() => setVehicleType("bike")} className={`h-16 rounded-xl border-2 flex flex-col items-center justify-center gap-1 font-semibold text-sm transition-all ${vehicleType === "bike" ? "border-[#16a34a] bg-[#16a34a]/10" : "border-[#E2DFD8]"}`}>
                          <Bike className="w-5 h-5 text-[#16a34a]" /> {isEuropeDriver ? "Vélo (Colis)" : t("delivery.modeVelo")}
                        </button>
                      </div>
                      {vehicleType === "bike" && <p data-testid="bike-no-license" className="mt-2 text-xs text-[#16a34a] font-medium flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {t("drv.bikeNoLicense")}</p>}
                      {vehicleType === "moto" && <p data-testid="moto-no-license" className="mt-2 text-xs text-[#D94F2B] font-medium flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {t("drv.motoNoLicense")}</p>}
                      {!taxiDocsRequired && <p data-testid="car-docs-optional" className="mt-2 text-xs text-[#2563EB] font-medium flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {t("drv.taxiDocsOptional")}</p>}
                    </div>
                    {[
                      { key: "id_card_doc", label: isEuropeDriver ? "Carte d'identité recto · ID (voorkant) · ID (front)" : t("drv.idCardFront"), optional: !taxiDocsRequired },
                      { key: "id_card_back_doc", label: isEuropeDriver ? "Carte d'identité verso · ID (achterkant) · ID (back)" : t("drv.idCardBack"), optional: !taxiDocsRequired },
                      ...(vehicleType === "car" ? [{ key: "license_doc", label: isEuropeDriver ? "Permis de conduire · Rijbewijs · Driving licence" : t("drv.license"), optional: !taxiDocsRequired && !carDocsRequired }] : []),
                      ...(vehicleType !== "bike" ? [{ key: "vehicle_doc", label: isEuropeDriver ? "Carte grise · Kentekenbewijs · Vehicle registration" : t("drv.vehicle"), optional: !taxiDocsRequired && !carDocsRequired }] : []),
                    ].map((d) => (
                      <div key={d.key} data-testid={`upload-${d.key}`} className="rounded-xl border-2 border-dashed border-[#E2DFD8] p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{d.label}{d.optional && <span className="text-[#75807A] font-normal"> · {t("drv.optional")}</span>}</span>
                          {docs[d.key] && <span data-testid={`ok-${d.key}`} className="flex items-center gap-1 text-sm text-[#D94F2B]"><Check className="w-4 h-4" />{t("drv.uploaded")}</span>}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <label data-testid={`scan-${d.key}`} className="flex items-center justify-center gap-1.5 h-11 rounded-lg bg-[#F26419]/10 text-[#D94F2B] text-sm font-semibold cursor-pointer hover:bg-[#F26419]/20 transition-colors">
                            <Camera className="w-4 h-4" /> {t("drv.scan")}
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pickFile(d.key)} />
                          </label>
                          <label data-testid={`import-${d.key}`} className="flex items-center justify-center gap-1.5 h-11 rounded-lg bg-[#EFEBE4] text-[#3B423E] text-sm font-semibold cursor-pointer hover:bg-[#E2DFD8] transition-colors">
                            <Upload className="w-4 h-4" /> {t("drv.import")}
                            <input type="file" accept="image/*,.pdf" className="hidden" onChange={pickFile(d.key)} />
                          </label>
                        </div>
                      </div>
                    ))}
                    <label data-testid="upload-selfie" className="flex items-center justify-between h-14 px-4 rounded-xl border-2 border-dashed border-[#E2DFD8] cursor-pointer hover:border-[#D94F2B] transition-colors">
                      <span className="text-sm font-medium">{t("drv.selfie")}{!taxiDocsRequired && <span className="text-[#75807A] font-normal"> · {t("drv.optional")}</span>}</span>
                      <span className={`flex items-center gap-1.5 text-sm ${docs.selfie ? "text-[#D94F2B]" : "text-[#75807A]"}`}>
                        {docs.selfie ? <><Check className="w-4 h-4" />{t("drv.uploaded")}</> : <><Camera className="w-4 h-4" />{t("drv.upload")}</>}
                      </span>
                      <input type="file" accept="image/*" capture="user" className="hidden" onChange={pickFile("selfie")} />
                    </label>
                    {isEuropeDriver && !addServiceMode && (
                      <div className="space-y-3" data-testid="eu-driver-fields">
                        <div>
                          <label className="text-sm font-medium text-[#3B423E]">IBAN <span className="text-[#75807A] font-normal">· uitbetalingen · payouts (€)</span></label>
                          <Input data-testid="kyc-iban" value={iban} onChange={(e) => setIban(e.target.value.toUpperCase())} placeholder="BE00 0000 0000 0000" className="mt-1.5 h-14 rounded-xl border-2" />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-[#3B423E]">Numéro de TVA · BTW-nummer · VAT number <span className="text-[#75807A] font-normal">· facultatif / optioneel / optional</span></label>
                          <Input data-testid="kyc-vat" value={vatNumber} onChange={(e) => setVatNumber(e.target.value.toUpperCase())} placeholder="BE 0123.456.789" className="mt-1.5 h-14 rounded-xl border-2" />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-[#3B423E]">Adresse · Adres · Address <span className="text-[#75807A] font-normal">· facultatif</span></label>
                          <Input data-testid="kyc-address" value={euAddress} onChange={(e) => setEuAddress(e.target.value)} placeholder="Rue, n°, code postal, ville" className="mt-1.5 h-14 rounded-xl border-2" />
                        </div>
                        <label data-testid="kyc-gdpr" className="flex items-start gap-3 rounded-2xl border-2 border-[#E2DFD8] p-3 cursor-pointer hover:border-[#2563EB] transition-colors">
                          <input type="checkbox" data-testid="kyc-gdpr-checkbox" checked={gdprAccepted} onChange={(e) => setGdprAccepted(e.target.checked)} className="mt-0.5 w-5 h-5 accent-[#2563EB] shrink-0" />
                          <span className="text-xs text-[#3B423E] leading-relaxed">
                            J'accepte la <a data-testid="gdpr-link" href="/confidentialite" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="font-semibold text-[#2563EB] underline">politique de confidentialité (RGPD)</a>.
                            <span className="block text-[#75807A]">Ik aanvaard het privacybeleid (GDPR). · I accept the privacy policy (GDPR).</span>
                          </span>
                        </label>
                      </div>
                    )}
                    {vehicleType !== "bike" && (
                      <div>
                        <label className="text-sm font-medium text-[#3B423E]">{t("drv.plate")}{!taxiDocsRequired && !carDocsRequired && <span className="text-[#75807A] font-normal"> · {t("drv.optional")}</span>}</label>
                        <Input data-testid="kyc-plate" value={plate} onChange={(e) => setPlate(e.target.value)} placeholder={t("drv.platePh")} className="mt-1.5 h-14 rounded-xl border-2" />
                        {docs.vehicle_doc?.startsWith("data:image/") && (
                          <button type="button" data-testid="extract-plate" onClick={extractPlate} disabled={extracting} className="mt-2 text-sm font-semibold text-[#D94F2B] inline-flex items-center gap-1 disabled:opacity-50">
                            <Sparkles className="w-4 h-4" /> {extracting ? t("drv.plateReading") : t("drv.plateExtract")}
                          </button>
                        )}
                      </div>
                    )}
                    <Button data-testid="submit-kyc" onClick={submitKyc} disabled={kycSubmitting} className="w-full h-14 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-lg disabled:opacity-60">{kycSubmitting ? "Envoi en cours…" : t("drv.submitKyc")}</Button>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {tab === "abo" && (
            <div className="mt-6 space-y-4" data-testid="subscription-section">
              {sub?.suspended && (
                <div data-testid="sub-suspended" className="rounded-2xl bg-[#D32F2F]/10 border-2 border-[#D32F2F] p-5">
                  <div className="font-head font-bold text-[#D32F2F]">{t("drv.subSuspendedTitle")}</div>
                  <p className="mt-1 text-sm text-[#3B423E]">{sub.suspension_reason}</p>
                </div>
              )}
              <div className="rounded-3xl bg-[#1A1A1A] text-white p-6" data-testid="caution-card">
                <div className="text-xs uppercase tracking-wide text-[#F26419] font-semibold">Modèle Dépôt de garantie</div>
                <div className="mt-3 flex items-end gap-2">
                  <span data-testid="caution-wallet" className="font-head font-black text-4xl">{(sub?.wallet ?? me?.wallet ?? 0).toLocaleString("fr-FR")}</span>
                  <span className="mb-1 text-white/60">FCFA de solde</span>
                </div>
                <div className="mt-2 text-sm">
                  <span className={`px-2 py-0.5 rounded-full font-semibold ${sub?.active ? "bg-[#16a34a] text-white" : "bg-[#D32F2F] text-white"}`}>{sub?.active ? "Actif" : "Inactif"}</span>
                </div>
                <div className="mt-4 rounded-2xl bg-white/10 border border-[#F26419]/40 p-4 text-left text-sm text-white/85">
                  <p>Activation unique : <b className="text-white">{(sub?.caution || 600).toLocaleString("fr-FR")} FCFA</b> (payée une seule fois)</p>
                  <p className="mt-1">Commission prélevée : <b className="text-white">19 %</b> par course terminée (portefeuille → trésorerie admin).</p>
                  <p className="mt-2 text-xs text-white/60">{sub?.free ? "Provisionnez votre portefeuille pour couvrir les 19 % de commission prélevés à chaque course. Pendant la promo, les courses continuent d'arriver même à solde bas." : "Gardez un solde positif. Rechargez votre portefeuille dès qu'il baisse pour continuer à recevoir des courses."}</p>
                  {sub?.active && (
                    <button data-testid="sub-receipt-btn" onClick={async () => { try { const r = await api.get('/driver/subscription/receipt.pdf', { responseType: 'blob' }); const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = 'recu-activation.pdf'; a.click(); URL.revokeObjectURL(url); } catch (e) { toast.error("Aucun reçu disponible pour le moment."); } }} className="mt-3 w-full h-10 rounded-xl bg-white/10 border border-white/20 text-white text-sm font-semibold hover:bg-white/20 transition-colors">Télécharger mon reçu d'activation (PDF)</button>
                  )}
                </div>
              </div>
              <div className="rounded-2xl bg-[#F26419]/12 border border-[#F26419] p-4 text-sm text-[#3B423E]">⚠️ {t("drv.subRules")}</div>

              <div className="rounded-3xl bg-white border border-[#E2DFD8] p-6" data-testid="sub-history">
                <h3 className="font-head font-bold text-lg text-[#1A1A1A]">{t("drv.subHistory")}</h3>
                {subHistory.length === 0 ? (
                  <p data-testid="sub-history-empty" className="mt-3 text-sm text-[#75807A]">{t("drv.subHistoryEmpty")}</p>
                ) : (
                  <div className="mt-4 divide-y divide-[#E2DFD8]">
                    {subHistory.map((h, i) => (
                      <div key={h.id || i} data-testid={`sub-history-item-${i}`} className="flex items-center justify-between py-3">
                        <div>
                          <div className="font-semibold text-[#1A1A1A]">{(h.amount || 0).toLocaleString("fr-FR")} FCFA</div>
                          <div className="text-xs text-[#75807A]">{h.created_at ? new Date(h.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : ""}</div>
                        </div>
                        {h.mocked && <span className="text-[10px] uppercase tracking-wide px-2 py-1 rounded-full bg-[#F0EBE1] text-[#75807A] font-semibold">{t("drv.subHistoryMocked")}</span>}
                        <button data-testid={`sub-receipt-${i}`} onClick={() => downloadSubReceipt(h)} className="h-8 px-3 rounded-lg bg-[#F0EBE1] hover:bg-[#F26419] text-xs font-semibold flex items-center gap-1 transition-colors"><Wallet className="w-3.5 h-3.5" /> {t("drv.subReceipt")}</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "profile" && (
            <motion.div key="prof" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6">
              <div className="bg-white rounded-3xl border border-[#E2DFD8] p-7">
                <div className="w-16 h-16 rounded-2xl bg-[#F26419] flex items-center justify-center text-white font-head font-black text-2xl">{(me?.name || "C")[0].toUpperCase()}</div>
                <h2 className="mt-4 font-head font-bold text-xl">{me?.name}</h2>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex justify-between border-b border-[#E2DFD8] pb-2"><span className="text-[#75807A]">{t("pax.phone")}</span><span className="font-medium">{me?.phone}</span></div>
                  <div className="flex justify-between border-b border-[#E2DFD8] pb-2"><span className="text-[#75807A]">KYC</span><span className="font-medium capitalize" style={{ color: b.c }}>{status}</span></div>
                  <div className="flex justify-between"><span className="text-[#75807A]">{t("drv.rating")}</span><span className="font-medium">{(me?.rating || 0).toFixed(1)} ★</span></div>
                </div>
              </div>
              <div className="mt-4 bg-white rounded-3xl border border-[#E2DFD8] p-6" data-testid="qr-settings">
                <h3 className="font-head font-bold text-base flex items-center gap-2"><QrCode className="w-5 h-5 text-[#D94F2B]" /> {t("pay.qrTitle")}</h3>
                <p className="text-xs text-[#75807A] mt-1">{t("pay.qrDesc")}</p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {[{ k: "orange", label: "Orange Money", color: "#FF6600" }, { k: "mtn", label: "MTN MoMo", color: "#8a6d00" }].map((o) => (
                    <div key={o.k} className="rounded-2xl border-2 border-[#E2DFD8] p-3 text-center">
                      <div className="text-xs font-bold mb-2" style={{ color: o.color }}>{o.label}</div>
                      {qr[o.k] ? (
                        <img src={qr[o.k]} alt={o.label} data-testid={`qr-preview-${o.k}`} className="w-28 h-28 object-contain mx-auto rounded-lg border border-[#E2DFD8]" />
                      ) : (
                        <div className="w-28 h-28 mx-auto rounded-lg bg-[#F7F3EC] flex items-center justify-center text-[#B8B2A7]"><QrCode className="w-10 h-10" /></div>
                      )}
                      <input data-testid={`qr-num-${o.k}`} value={qrNum[o.k]} onChange={(e) => setQrNum((n) => ({ ...n, [o.k]: e.target.value }))} placeholder={t("pay.numberLabel")} inputMode="tel" className="mt-2 w-full h-9 px-2 rounded-lg border border-[#E2DFD8] text-sm text-center focus:border-[#D94F2B] outline-none" />
                      <button data-testid={`qr-gen-${o.k}`} onClick={() => genQr(o.k)} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-[#F26419] text-white text-xs font-bold hover:bg-[#D85312]">
                        <QrCode className="w-3.5 h-3.5" /> {t("pay.generateQr")}
                      </button>
                      <label data-testid={`qr-upload-${o.k}`} className="mt-2 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-[#F26419]/10 text-[#D94F2B] text-xs font-semibold cursor-pointer hover:bg-[#F26419]/20 w-full">
                        <Upload className="w-3.5 h-3.5" /> {qr[o.k] ? t("pay.qrReplace") : t("pay.qrUpload")}
                        <input type="file" accept="image/*" className="hidden" onChange={pickQr(o.k)} />
                      </label>
                    </div>
                  ))}
                </div>
                {qr.orange && qr.mtn && (
                  <div className="mt-4">
                    <div className="text-xs font-semibold text-[#75807A] mb-2">{t("pay.qrChoose")}</div>
                    <div className="grid grid-cols-2 gap-2">
                      <button data-testid="qr-active-orange" onClick={() => { const n = { ...qr, active: "orange" }; setQr(n); saveQr(n); }} className={`h-10 rounded-xl text-sm font-bold ${(qr.active || "orange") === "orange" ? "bg-[#FF6600] text-white" : "bg-[#EFEBE4] text-[#3B423E]"}`}>Orange Money</button>
                      <button data-testid="qr-active-mtn" onClick={() => { const n = { ...qr, active: "mtn" }; setQr(n); saveQr(n); }} className={`h-10 rounded-xl text-sm font-bold ${qr.active === "mtn" ? "bg-[#FFCC00] text-black" : "bg-[#EFEBE4] text-[#3B423E]"}`}>MTN MoMo</button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </div>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white/90 backdrop-blur-xl border-t border-[#E2DFD8] z-50">
        <div className="max-w-2xl mx-auto grid grid-cols-4">
          {tabs.map((tb) => (
            <button key={tb.id} data-testid={`nav-${tb.id}`} onClick={() => setTab(tb.id)} className={`py-3 flex flex-col items-center gap-1 ${tab === tb.id ? "text-[#D94F2B]" : "text-[#75807A]"}`}>
              <tb.icon className="w-5 h-5" /><span className="text-xs font-medium">{tb.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {chatOpen && active && (
        <ChatSheet rideId={active.id} role="driver" peerName={active.passenger_name || t("hero.passenger")} onClose={() => setChatOpen(false)} />
      )}

      {popupRide && (
        <div data-testid="ride-popup" className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-white rounded-3xl overflow-hidden shadow-2xl animate-in slide-in-from-bottom duration-200">
            <div className="bg-[#16a34a] px-5 py-4 text-white flex items-center justify-between">
              <div className="flex items-center gap-2 font-head font-black text-lg">
                {popupRide.type === "delivery" ? <Package className="w-5 h-5" /> : popupRide.vehicle_type === "car" ? <Car className="w-5 h-5" /> : <Bike className="w-5 h-5" />}
                Nouvelle course
              </div>
              <div data-testid="ride-popup-timer" className="w-11 h-11 rounded-full bg-white/25 flex items-center justify-center font-black text-lg tabular-nums">{popupLeft}</div>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-center gap-2 text-sm"><span className="w-2.5 h-2.5 rounded-full bg-[#F26419] shrink-0" /><span className="font-semibold text-[#1A1A1A]">{popupRide.pickup}</span></div>
              <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-[#D94F2B] shrink-0" /><span className="font-semibold text-[#1A1A1A]">{popupRide.destination}</span></div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-[#75807A]">{popupRide.distance_km} km · {t(`pay.${popupRide.payment_method}`)}</span>
                <span className="font-head font-black text-2xl text-[#1A1A1A]">{money(popupRide.price, popupRide.currency)}</span>
              </div>
              <div className="w-full h-2 rounded-full bg-[#E2DFD8] overflow-hidden">
                <div className="h-full bg-[#16a34a] transition-all duration-1000 ease-linear" style={{ width: `${(popupLeft / 15) * 100}%` }} />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-1">
                <button data-testid="popup-decline" onClick={() => decline(popupRide.id)} className="h-13 py-3.5 rounded-2xl border-2 border-[#D32F2F] text-[#D32F2F] font-bold text-base hover:bg-[#D32F2F]/5 transition-colors">Refuser</button>
                <button data-testid="popup-accept" onClick={() => accept(popupRide)} className="h-13 py-3.5 rounded-2xl bg-[#16a34a] hover:bg-[#128a3e] text-white font-bold text-base transition-colors">Accepter</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {rechargeModal && (
        <div data-testid="driver-recharge-modal" className="fixed inset-0 z-[2000] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => setRechargeModal(null)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head font-black text-xl text-[#1A1A1A]">Recharger via {rechargeModal === "mtn_momo" ? "MTN MoMo" : "Orange Money"}</h3>
            <div className="mt-4 rounded-2xl bg-[#FDF8F0] border border-[#E2DFD8] p-4">
              <div className="text-xs text-[#75807A]">1. Envoyez le montant à ce numéro marchand officiel 🔒 :</div>
              <div className="mt-1 flex items-center justify-between">
                <span data-testid="driver-merchant-number" className="font-head font-black text-2xl text-[#D94F2B] tracking-wide">
                  {(rechargeModal === "mtn_momo" ? rechargeInfo?.mtn_number : rechargeInfo?.orange_number) || "Numéro non configuré"}
                </span>
                {(rechargeModal === "mtn_momo" ? rechargeInfo?.mtn_number : rechargeInfo?.orange_number) && (
                  <button data-testid="driver-copy-merchant" onClick={() => { navigator.clipboard?.writeText(rechargeModal === "mtn_momo" ? rechargeInfo.mtn_number : rechargeInfo.orange_number); toast.success("Numéro copié"); }} className="text-xs font-semibold text-[#D94F2B] underline">Copier</button>
                )}
              </div>
              <div className="mt-1 text-[11px] text-[#75807A]">{rechargeInfo?.merchant_name || "Mboa Go"}</div>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-[#3B423E]">2. Montant versé (FCFA)</label>
                <Input data-testid="driver-rc-amount" type="number" inputMode="numeric" value={rcAmount} onChange={(e) => setRcAmount(e.target.value)} placeholder="Ex : 1000" className="mt-1.5 h-12 rounded-xl border-2" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#3B423E]">3. ID de transaction (reçu par SMS)</label>
                <Input data-testid="driver-rc-txid" value={rcTxId} onChange={(e) => setRcTxId(e.target.value)} placeholder="Ex : MP240612.1234.A56789" className="mt-1.5 h-12 rounded-xl border-2" />
                <p className="mt-1 text-[11px] text-[#75807A]">Copiez l'identifiant depuis le SMS de confirmation Mobile Money.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-[#3B423E]">4. Reçu Mobile Money (capture d'écran, obligatoire)</label>
                <label data-testid="driver-rc-proof-label" className="mt-1.5 flex items-center gap-3 rounded-xl border-2 border-dashed border-[#E2DFD8] p-3 cursor-pointer hover:border-[#D94F2B] transition-colors">
                  {rcProofPreview ? (
                    <img src={rcProofPreview} alt="reçu" className="w-12 h-12 object-cover rounded-lg" />
                  ) : (
                    <span className="w-12 h-12 rounded-lg bg-[#FDF8F0] flex items-center justify-center"><Smartphone className="w-5 h-5 text-[#75807A]" /></span>
                  )}
                  <span className="text-sm text-[#75807A]">{rcProofUploading ? "Envoi…" : rcProofPath ? "Reçu joint ✓ — changer" : "Joindre la capture pour accélérer la validation"}</span>
                  <input data-testid="driver-rc-proof-input" type="file" accept="image/*" onChange={uploadProof} className="hidden" />
                </label>
              </div>
              <Button data-testid="driver-rc-submit" onClick={submitRecharge} disabled={rcLoading || rcProofUploading} className="w-full h-12 rounded-xl bg-[#D94F2B] hover:-translate-y-0.5 transition-transform text-white font-bold">
                {rcLoading ? "Envoi…" : "Envoyer pour validation"}
              </Button>
              <button data-testid="driver-rc-cancel" onClick={() => setRechargeModal(null)} className="w-full text-sm text-[#75807A] hover:text-[#1A1A1A]">Annuler</button>
              <p className="text-[11px] text-[#75807A] text-center">Votre solde sera crédité après vérification par l'administrateur.</p>
            </div>
          </div>
        </div>
      )}
      {isSuperAdmin && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[4000] bg-white rounded-full shadow-xl border border-[#E2DFD8] px-3 py-2" data-testid="admin-zone-bar-driver">
          <AdminZoneSwitch />
        </div>
      )}
    </div>
  );
}
